/**
 * Ahrefs Adapter — Story 20.1 (opens Epic 20: SEO Integrations)
 *
 * **Auth model:** API-key Bearer token. The customer pastes their Ahrefs
 * Enterprise API key into the Connect dialog; we encrypt it via the same
 * `encryptToken` helper that OAuth tokens use and store it in `oauth_tokens`
 * under `provider="ahrefs"`. See AC #3 in the Story 20.1 spec for why we
 * deliberately reuse the OAuth table for an API key — the column shape is
 * identical (encrypted secret + optional expiry + per-tenant scoping).
 *
 * **Divergence from PageSpeed (`authType: "api-key"` companion):**
 *  - PageSpeed uses a SERVER-SIDE env-binding API key
 *    (`GOOGLE_PAGESPEED_API_KEY` set by Calin via `wrangler secret`). One
 *    key serves all tenants.
 *  - Ahrefs uses a PER-USER, PER-TENANT key. Every customer brings their own
 *    Enterprise key, encrypted at rest with a per-user HKDF-derived key.
 *    Therefore Ahrefs is NOT in `API_KEY_PLATFORMS` — that set is reserved
 *    for env-key platforms. This is the deliberate divergence noted in
 *    AC #9 / Task 1.10.
 *
 * **Future Ahrefs Connect OAuth (Story 20.1a) — design pre-wired:**
 *  - `fetchMetrics` reads `credentials.accessToken` and sends
 *    `Authorization: Bearer ${accessToken}`. The wire format is identical
 *    for both auth modes (per docs.ahrefs.com/docs/ahrefs-connect/developers/api-guide),
 *    so the OAuth flow ships as a SEPARATE family entry without touching the
 *    adapter. AC #16 documents this.
 *  - The future "ahrefs-connect" family will share this same adapter; only
 *    the credential acquisition path differs (token exchange via
 *    `https://ahrefs.com/oauth/token` PKCE-S256 flow vs. user-pasted key).
 *
 * **Unit-cost economics dominate every design decision:**
 *  - Ahrefs charges per row × field, not per request.
 *  - Default field-set returns ALL fields → 5×–10× the cost of a tight
 *    `select`. The helpers in `api.ts` throw `TypeError` synchronously on
 *    empty `select` arrays.
 *  - Cache TTL is 4h (vs. 1h for ad platforms) because Ahrefs data updates
 *    at-most-daily and aggressive caching conserves the customer's monthly
 *    Enterprise quota.
 *  - Per-tenant unit-budget circuit-breaker (5M units/day) caps runaway
 *    sync calls before they exhaust the customer's monthly cap.
 *
 * **v2 is dead.** The adapter targets `https://api.ahrefs.com/v3/*`
 * exclusively. v2 was discontinued 2025-11-01 with no fallback path.
 */

import type {
  IntegrationAdapter,
  AdapterContext,
  OAuthCredentials,
  AdapterResult,
  AdapterMetrics,
  AccountInfo,
  MetricDefinition,
  DimensionDefinition,
  EndpointDiscoveryInfo,
} from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import {
  AHREFS_API_VERSION,
  AHREFS_API_BASE,
  fetchAhrefsDomainRating,
  fetchAhrefsBacklinksStats,
  fetchAhrefsMetrics,
  fetchAhrefsBatchAnalysis,
  fetchAhrefsMetricsHistory,
  METRICS_HISTORY_DEFAULT_SELECT,
  validateAhrefsConnection,
  // Story 20.2e — Brand Radar add-on detection probe
  fetchAhrefsBrandRadarSovOverview,
  type AhrefsCallResult,
} from "./api";
import {
  ahrefsErrorTypeToAdapterCode,
  parseAhrefsApiError,
  sanitizeAhrefsErrorMessage,
  // Code Review Pass 3 (P3-L7) — single source of truth for BR-403 detection;
  // probe uses the SAME regex as parseAhrefsApiError to avoid drift.
  BRAND_RADAR_SUBSCRIPTION_REGEX,
} from "./errors";
import { inferAhrefsMode, type AhrefsMode } from "./normalize";
import {
  transformAhrefsDomainRatingResponse,
  transformAhrefsBacklinksStatsResponse,
  transformAhrefsMetricsResponse,
  transformAhrefsBatchAnalysisResponse,
  transformAhrefsMetricsHistoryResponse,
} from "./transformers";
import {
  checkAhrefsUnitBudget,
  incrementAhrefsUnitsUsed,
  AHREFS_DAILY_TENANT_UNIT_BUDGET,
} from "./unit-budget";
import type { KVStore } from "../cache";

// ============================================================================
// Constants
// ============================================================================

const PLATFORM = "ahrefs" as const;

/** Re-exports for the public surface (AC #9). */
export { AHREFS_API_VERSION, AHREFS_API_BASE };
export const AHREFS_PLATFORM_VALUES = ["ahrefs"] as const;
export type AhrefsPlatform = (typeof AHREFS_PLATFORM_VALUES)[number];

/**
 * Story 20.9 — canonical GreenLife demo target for cross-SEO snapshot
 * seeding. Mirrors `MOZ_DEMO_SNAPSHOT_TARGET` at `moz.ts:158` per Story
 * 20.8 Pass 2 BH2-8 cross-app-drift-defense convention (single source of
 * truth shared between `demo-seed.ts` INSERT and `widgetPresets.ts`
 * `dataSource.filter.target`).
 */
export const AHREFS_DEMO_SNAPSHOT_TARGET = "greenlifewellness.com";

/** Number of monthly snapshots seeded per Story 20.9 AC #6 + AC #7. */
export const AHREFS_DEMO_SNAPSHOT_ROW_COUNT = 6;

/**
 * Story 20.2b — single source of truth for synthetic metric keys.
 *
 * Synthetic metrics are computed in the transformer from raw fields rather
 * than fetched directly from the Ahrefs wire. They MUST NOT carry an
 * `apiField`, and the metric-allowlist tests + AHREFS_METRICS shape
 * invariants exempt them from the apiField requirement.
 *
 * Defining the set here (not in the test files) ensures any rename of a
 * synthetic key updates BOTH the runtime check AND the test exemption in
 * lockstep — preventing a future agent from silently breaking the
 * "synthetic must have no apiField" invariant.
 */
export const AHREFS_SYNTHETIC_METRIC_KEYS = new Set<string>([
  "anchorDiversityRatio",
  "educationalRefDomainPct",
  "governmentalRefDomainPct",
  // Story 20.2c — synthetic NaN-safe derived metrics for /rank-tracker/overview.
  // `positionMomentum`: signed integer (positive = moved up, lower SERP
  // position is better), null on missing/non-finite/frozen-keyword inputs.
  // `appearedToday`: boolean — keyword newly appeared on `date` after being
  // absent on `date_compared` (or fresh data with no compared date).
  "positionMomentum",
  "appearedToday",
]);

/**
 * Default `select` array for `/batch-analysis`.
 *
 * Story 20.1 shipped 18 fields (cost ≈ 50–180 units per row).
 * Story 20.2b widens to 33 fields (+15) so the agency-MVP dashboard surfaces
 * .gov/.edu refdomains, anchor diversity, redirect backlinks, and crawl-depth
 * signals competitors (AgencyAnalytics, Whatagraph) ship by default.
 *
 * **Cost ceiling (Story 20.2b AC #4):** 33-field default × 1-target ≈
 * `max(50, (28 + 10×5) × 1) = 78 units` minimum, up to `78 × N targets` for
 * batch sync. The five 10-unit fields in this select are the four 10-unit
 * traffic columns from Story 20.1 (`org_traffic`, `org_cost`-equivalents
 * via `org_keywords_*` cousins) PLUS the new edu/gov backlink / refdomain
 * fields. Within the per-tenant 5M units/day budget at ≤60K targets/day.
 *
 * **Single endpoint** replaces stitching `/metrics` + `/domain-rating` +
 * `/backlinks-stats` for "at-a-glance" dashboard widgets.
 *
 * ⚠️ **VERIFICATION STATUS (Story 20.2b code review pass 3, 2026-05-03):**
 * Public docs.ahrefs.com web reference for `/batch-analysis` documents only
 * the 18 Story-20.1 baseline fields PLUS `backlinks_internal`,
 * `backlinks_redirect`, `linked_domains_dofollow`, and
 * `outgoing_links_dofollow` — i.e. 22 of these 33 fields are publicly
 * documented. The remaining 11 fields (text/image/.gov/.edu anchors,
 * refclass-c subnets, dofollow/nofollow link breakdowns, crawl/live pages)
 * are listed in our internal IMC-5 catalogue
 * (`_bmad/bmm/data/integration-metrics/ahrefs.md`) which sourced from the
 * Ahrefs Python SDK schema (`ahrefs-python/src/ahrefs/types/_generated.py`)
 * — a more complete reference than the public web docs (Ahrefs's web docs
 * are incomplete by Ahrefs's own admission).
 *
 * **Known risk:** if Ahrefs rejects any of the 11 unverified fields with a
 * 400 error, the entire batch-analysis call fails and the sync emits a
 * `network_error` per the existing `safeBa.catch` handler in fetchMetrics.
 * Mitigation: the catch wraps the failure as a partial-sync warning rather
 * than failing the whole adapter — the other 4 endpoints still return data.
 * Recommended pre-deploy: canary-call this select against a real Ahrefs
 * Enterprise key on staging before promoting to production.
 */
export const AHREFS_BATCH_ANALYSIS_DEFAULT_SELECT: ReadonlyArray<string> = [
  // Story 20.1 baseline (18 fields)
  "domain_rating",
  "url_rating",
  "backlinks",
  "backlinks_dofollow",
  "backlinks_nofollow",
  "refdomains",
  "refdomains_dofollow",
  "refdomains_nofollow",
  "org_traffic",
  "org_keywords",
  "org_keywords_1_3",
  "org_keywords_4_10",
  "paid_traffic",
  "paid_keywords",
  "refips",
  "refips_subnets",
  "linked_domains",
  "outgoing_links",
  // Story 20.2b expansion (+15 fields) — alphabetised within category.
  // Backlink-type breakdown (anchor diversity + redirect-chain investigation):
  "backlinks_educational",
  "backlinks_governmental",
  "backlinks_image",
  "backlinks_internal",
  "backlinks_redirect",
  "backlinks_text",
  // Refdomain composition (.gov/.edu high-trust; C-class subnets sharper than
  // refips alone for detecting link-network manipulation):
  "refclass_c_subnets",
  "refdomains_educational",
  "refdomains_governmental",
  // Outgoing-link composition (target's authority distribution):
  "linked_domains_dofollow",
  "linked_domains_nofollow",
  "outgoing_links_dofollow",
  "outgoing_links_nofollow",
  // Crawl-depth signals (page footprint):
  "crawled_pages",
  "live_pages",
  // Story 35.8 expansion (+6 unique new wire fields) — Batch Analysis
  // residual long-tail. Per docs.ahrefs.com/api/reference/site-explorer/get-batch-analysis
  // verified 2026-05-19. The 3 paid fields (`org_cost`,
  // `org_traffic_top_by_country`, `paid_cost`) add ~30 units per call per
  // target — within the existing per-tenant 5M/day budget. The 3 free
  // fields (`ahrefs_rank`, `ip`, `paid_ads`) add zero cost.
  // `org_traffic_top_by_country` ships as opaque-array (Array<{country, traffic}>);
  // per-element transformer deferred to Story 20.4 widget surface.
  //
  // Note: Story 35.8 spec line AC #3 listed 7 wire fields including
  // `org_traffic`, but `org_traffic` is ALREADY in the Story 20.1 baseline
  // (line above) — including it again would violate the no-duplicates
  // invariant. The catalogue still ships +7 MetricDefinitions (Batch 3 of
  // 4), as `batchOrganicTraffic` is a distinct MetricDefinition surfacing
  // the SAME wire field for the batch-analysis preset.
  "ahrefs_rank",
  "ip",
  "org_cost",
  "org_traffic_top_by_country",
  "paid_ads",
  "paid_cost",
];

// ============================================================================
// Story 20.2 — Autonomous sync surface vs. widget-config-driven surface (AC #6)
// ============================================================================
//
// The 4 Story 20.1 endpoints (`domain-rating`, `backlinks-stats`, `metrics`,
// `batch-analysis`) are part of the AUTONOMOUS sync surface — they fire on
// every cron-driven sync because they have safe defaults (target+mode are
// known from the integration row).
//
// The 4 Story 20.2 endpoints (`keywords-explorer-overview`, `organic-keywords`,
// `rank-tracker-competitors-stats`, `keywords-history`) are WIDGET-CONFIG-DRIVEN.
// They are NOT called from `fetchMetrics`. Reasons:
//   (a) `keywords-explorer-overview` requires a customer-supplied keyword list.
//   (b) `organic-keywords` returns up to 1000 rows × 110 units/row — autonomous-
//       cost-prohibitive at the customer's expense.
//   (c) `rank-tracker-competitors-stats` requires a `project_id` from the
//       customer's Rank Tracker UI; we don't know it without explicit selection.
//   (d) `keywords-history` requires a widget-config-specific `date_from`.
//
// Story 20.2 exposes these endpoints via proxy routes
// (`apps/server/src/routes/integrations/ahrefs.ts`) that the future widget
// rendering layer (Story 20.5) calls per widget config.
//
// ============================================================================
// Metric Definitions (AC #11 from Story 20.1 — 30 priority metrics; AC #1
// from Story 20.2 — +50 priority metrics across 4 NEW endpoint groups)
// ============================================================================

/**
 * 30 priority metrics across the 4 endpoint groups in AC #4 from Story 20.1
 * + 50 priority metrics from Story 20.2 across 4 NEW endpoint groups
 * (Keywords Explorer Overview, Organic Keywords, Rank Tracker Competitors
 * Stats, Keywords History). Total: 80 metrics live, covering 42.8% of the
 * 187-metric `_bmad/bmm/data/integration-metrics/ahrefs.md` catalogue.
 *
 * **Dual-naming rule (Story 20.2 AC #14):** for metrics that exist in
 * multiple Ahrefs endpoints under different wire-format names (e.g.
 * Keywords Explorer's `difficulty` vs. Organic Keywords' `keyword_difficulty`),
 * the metric `key` uses the synthetic camelCase form (`keywordDifficulty`)
 * AND the `apiField` uses the wire-format name from the endpoint we
 * primarily source the metric from. The transformers ALSO emit synthetic
 * mirrors so widgets can dispatch on a single key regardless of source.
 *
 * Per-endpoint allowlist note: every entry's `apiField` MUST appear in the
 * relevant endpoint's `select` array (see `api.ts`). The build-time test in
 * `metric-allowlist.test.ts` enforces this — catches typos like
 * `domain_rating` → `Domain_Rating` immediately at CI time.
 */
export const AHREFS_METRICS: MetricDefinition[] = [
  // Domain Rating Group (2)
  {
    key: "domainRating",
    label: "Domain Rating",
    type: "number",
    category: "score",
    dataType: "Decimal",
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
    apiField: "domain_rating",
    description: "Ahrefs Domain Rating (0–100, logarithmic). The single most-loved SEO metric — appears on every agency client report.",
  },
  {
    key: "ahrefsRank",
    label: "Ahrefs Rank",
    type: "number",
    category: "score",
    dataType: "Integer",
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi"],
    apiField: "ahrefs_rank",
    description: "Global rank where #1 is the strongest backlink profile in Ahrefs' index.",
  },

  // Backlinks Group (8)
  {
    key: "liveBacklinks",
    label: "Live Backlinks",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "live",
    description: "Number of currently live backlinks pointing to the target.",
  },
  {
    key: "allTimeBacklinks",
    label: "All-Time Backlinks",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "all_time",
    description: "Cumulative count of all backlinks ever discovered (live + lost).",
  },
  {
    key: "liveReferringDomains",
    label: "Live Referring Domains",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "live_refdomains",
    description: "Distinct domains currently linking to the target.",
  },
  {
    key: "allTimeReferringDomains",
    label: "All-Time Referring Domains",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "all_time_refdomains",
    description: "Cumulative count of all referring domains ever discovered.",
  },
  {
    key: "backlinksDofollow",
    label: "Dofollow Backlinks",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "backlinks_dofollow",
    description: "Backlinks passing PageRank (no `rel=nofollow`).",
  },
  {
    key: "backlinksNofollow",
    label: "Nofollow Backlinks",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "backlinks_nofollow",
    description: "Backlinks marked `rel=nofollow` — do not pass authority.",
  },
  {
    key: "referringIps",
    label: "Referring IPs",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "refips",
    description: "Distinct IP addresses hosting referring domains. Used to detect link-network manipulation.",
  },
  {
    key: "referringIpSubnets",
    label: "Referring IP Subnets",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "refips_subnets",
    description: "Distinct /24 subnets hosting referring domains. Stronger signal than refips alone.",
  },

  // Organic Traffic Group (8)
  {
    key: "organicKeywords",
    label: "Organic Keywords",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "org_keywords",
    description: "Number of keywords the target ranks for in Ahrefs' database.",
  },
  {
    key: "organicKeywordsTop3",
    label: "Organic Keywords (Top 3)",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "org_keywords_1_3",
    description: "Keywords ranking in positions 1–3.",
  },
  {
    key: "organicKeywordsPositions4_10",
    label: "Organic Keywords (Positions 4–10)",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "org_keywords_4_10",
    description: "Keywords ranking in positions 4–10 — quick-win expansion candidates.",
  },
  // Story 20.3 — 3 NEW position-distribution buckets (catalogue gap → live).
  // Used by `ahrefs-keyword-position-distribution` preset.
  {
    key: "organicKeywordsPositions11_20",
    label: "Keywords in Positions 11–20",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "org_keywords_11_20",
    description: "Keywords ranking in positions 11–20 — page-2 keywords (next-target band for SEO investment).",
  },
  {
    key: "organicKeywordsPositions21_50",
    label: "Keywords in Positions 21–50",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "org_keywords_21_50",
    description: "Keywords ranking in positions 21–50 — long-tail tail keywords with future-quarter ranking potential.",
  },
  {
    key: "organicKeywordsPositions51Plus",
    label: "Keywords in Positions 51+",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "org_keywords_51_plus",
    description: "Keywords ranking past position 50 — minimal-traffic but content-discovery indicators.",
  },
  {
    key: "organicTraffic",
    label: "Organic Traffic",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "org_traffic",
    description: "Estimated monthly organic search traffic.",
  },
  {
    key: "organicTrafficValue",
    label: "Organic Traffic Value",
    type: "currency",
    category: "search",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "org_cost",
    description: "USD value of the equivalent paid traffic. Returned as cents and converted to dollars by the adapter.",
  },
  {
    key: "organicCompetitors",
    label: "Organic Competitors",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "linked_domains",
    description: "Distinct domains the target organically competes with for keyword positions.",
  },
  {
    key: "urlRating",
    label: "URL Rating",
    type: "number",
    category: "score",
    dataType: "Decimal",
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "url_rating",
    description: "Page-level authority (0–100). Distinct from Domain Rating, which is host-level.",
  },
  {
    key: "topPagesByTraffic",
    label: "Top Pages by Traffic",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "org_traffic",
    description: "Per-page organic traffic breakdown — surfaced via batch-analysis target list.",
  },

  // Paid Traffic Group (4)
  {
    key: "paidKeywords",
    label: "Paid Keywords",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "paid_keywords",
    description: "Keywords the target's domain currently bids on in Google Ads.",
  },
  {
    key: "paidTraffic",
    label: "Paid Traffic",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "paid_traffic",
    description: "Estimated monthly paid search traffic.",
  },
  {
    key: "paidTrafficCost",
    label: "Paid Traffic Cost",
    type: "currency",
    category: "search",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "paid_cost",
    description: "USD value of the target's paid search spend. Returned as cents and converted to dollars by the adapter.",
  },
  {
    key: "paidPages",
    label: "Paid Pages",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "paid_pages",
    description: "Number of distinct landing pages receiving paid search traffic.",
  },

  // Batch Analysis Aggregate Group (8)
  {
    key: "linkedDomains",
    label: "Linked Domains",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "linked_domains",
    description: "Distinct domains the target links OUT to (outgoing link footprint).",
  },
  {
    key: "linkedDomainsDofollow",
    label: "Linked Domains (Dofollow)",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    // Story 20.2b — apiField corrected from `linked_domains` (Story 20.1's
    // copy-paste bug — that field is the ALL-TYPES total, not dofollow-only)
    // to the dedicated `linked_domains_dofollow` field now in the default
    // select. NOTE: this is a quiet wire-format change for any pre-Story-20.2b
    // widget that bound to this metric — the SAME `key` now surfaces a
    // different (and correct) numeric source. No customer widgets shipped
    // pre-20.2b on this metric since Story 20.5 widget rendering hasn't
    // shipped yet, so the "breaking" nature is theoretical.
    apiField: "linked_domains_dofollow",
    description: "Domains the target links to via dofollow (passes authority).",
  },
  {
    key: "linkedDomainsNofollow",
    label: "Linked Domains (Nofollow)",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "linked_domains_nofollow",
    description: "Domains the target links to via nofollow (does not pass authority).",
  },
  {
    key: "outgoingLinks",
    label: "Outgoing Links",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "outgoing_links",
    description: "Total outgoing links from the target (across all linked domains).",
  },
  {
    key: "outgoingLinksDofollow",
    label: "Outgoing Links (Dofollow)",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    // Story 20.2b — apiField corrected from `outgoing_links` (Story 20.1
    // copy-paste bug — that's the ALL-TYPES outgoing total) to the
    // `outgoing_links_dofollow` field now in the default select. Same
    // theoretical-breaking-change note as `linkedDomainsDofollow` above.
    apiField: "outgoing_links_dofollow",
    description: "Outgoing links marked dofollow.",
  },
  {
    key: "outgoingLinksNofollow",
    label: "Outgoing Links (Nofollow)",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "outgoing_links_nofollow",
    description: "Outgoing links marked nofollow.",
  },
  // Story 20.2b — `internalBacklinks` + `redirectBacklinks` are now LIVE.
  // Story 20.1 marked them deprecated because their `apiField` values
  // (`backlinks_internal` / `backlinks_redirect`) weren't in the default
  // select. Story 20.2b widens the select; the deprecation reason is gone.
  {
    key: "internalBacklinks",
    label: "Internal Backlinks",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "backlinks_internal",
    description:
      "Internal backlinks within the target's own domain. Useful for evaluating internal-link strength on large content sites.",
  },
  {
    key: "redirectBacklinks",
    label: "Redirect Backlinks",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "backlinks_redirect",
    description:
      "Backlinks that flow through HTTP redirects. High percentages may indicate redirect-chain SEO issues worth investigating.",
  },
  // Story 20.2b — anchor-type breakdown (text vs image) for diversity ratio.
  {
    key: "backlinksText",
    label: "Backlinks (Text Anchor)",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "backlinks_text",
    description: "Backlinks whose anchor is plain text. Used in the anchor-diversity ratio.",
  },
  {
    key: "backlinksImage",
    label: "Backlinks (Image Anchor)",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "backlinks_image",
    description: "Backlinks whose anchor is an `<img>`. Image-heavy anchor profiles can be a manipulation signal.",
  },
  // Story 20.2b — high-trust domain signals (.gov / .edu).
  {
    key: "backlinksGovernmental",
    label: "Backlinks (.gov)",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "backlinks_governmental",
    description: "Backlinks from `.gov` domains — high-trust signal that boosts E-E-A-T.",
  },
  {
    key: "backlinksEducational",
    label: "Backlinks (.edu)",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "backlinks_educational",
    description: "Backlinks from `.edu` domains — high-trust signal valued in academic / research-driven verticals.",
  },
  {
    key: "referringDomainsGovernmental",
    label: "Referring Domains (.gov)",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "refdomains_governmental",
    description: "Distinct `.gov` referring domains. Headline at-a-glance signal for trust-profile reporting.",
  },
  {
    key: "referringDomainsEducational",
    label: "Referring Domains (.edu)",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "refdomains_educational",
    description: "Distinct `.edu` referring domains. Headline at-a-glance signal for trust-profile reporting.",
  },
  // Story 20.2b — sharper-than-refips link-network detection.
  {
    key: "refClassCSubnets",
    label: "Class-C Subnets",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "refclass_c_subnets",
    description: "Distinct /24 (Class-C) subnets hosting referring domains. Sharper signal than `refips` for detecting link-network manipulation.",
  },
  // Story 20.2b — crawl-depth signals.
  {
    key: "crawledPages",
    label: "Crawled Pages",
    type: "number",
    category: "content",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "crawled_pages",
    description: "Total pages of the target Ahrefs has crawled.",
  },
  {
    key: "livePages",
    label: "Live Pages",
    type: "number",
    category: "content",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "live_pages",
    description: "Currently-live page count for the target. Difference vs. crawled_pages indicates dead/redirected pages.",
  },
  // Story 20.2b — synthetic ratio metrics (NaN-safe in transformer).
  // No `apiField` because these are computed from raw fields — see metric-
  // allowlist test for the explicit synthetic exemption.
  {
    key: "anchorDiversityRatio",
    label: "Anchor Diversity Ratio",
    type: "percentage",
    category: "links",
    dataType: "Ratio",
    aggregationKind: "ratio", ratioComponents: { denominator: "liveBacklinks" },
    compatibleWidgets: ["kpi", "gauge"],
    description: "Synthetic — `backlinks_text / (backlinks_text + backlinks_image)`. Higher = more text-anchor diversity. NaN-safe (null when divisor is 0).",
  },
  {
    key: "educationalRefDomainPct",
    label: ".edu Referring-Domain %",
    type: "percentage",
    category: "links",
    dataType: "Ratio",
    aggregationKind: "ratio", ratioComponents: { numerator: "referringDomainsEducational", denominator: "liveReferringDomains" },
    compatibleWidgets: ["kpi", "gauge"],
    description: "Synthetic — `refdomains_educational / refdomains_dofollow`. Headline trust signal. NaN-safe (null when divisor is 0).",
  },
  {
    key: "governmentalRefDomainPct",
    label: ".gov Referring-Domain %",
    type: "percentage",
    category: "links",
    dataType: "Ratio",
    aggregationKind: "ratio", ratioComponents: { numerator: "referringDomainsGovernmental", denominator: "liveReferringDomains" },
    compatibleWidgets: ["kpi", "gauge"],
    description: "Synthetic — `refdomains_governmental / refdomains_dofollow`. Headline trust signal. NaN-safe (null when divisor is 0).",
  },
  {
    key: "referringDomainsDofollow",
    label: "Referring Domains (Dofollow)",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "refdomains_dofollow",
    description: "Referring domains linking via dofollow.",
  },
  {
    key: "referringDomainsNofollow",
    label: "Referring Domains (Nofollow)",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "refdomains_nofollow",
    description: "Referring domains linking via nofollow.",
  },

  // ==========================================================================
  // Story 20.2 — Keywords Explorer Overview (+18 metrics) [AC #1, #16]
  // ==========================================================================
  {
    key: "keywordsExplorerKeyword",
    label: "Keyword",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "keyword",
    description: "The keyword being researched (Keywords Explorer).",
  },
  {
    key: "keywordsExplorerClicks",
    label: "Keyword Clicks (KE)",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart", "data-table"],
    apiField: "clicks",
    description: "Avg monthly organic + paid clicks across all SERP positions for the keyword.",
  },
  {
    key: "costPerClick",
    label: "Cost Per Click",
    type: "currency",
    category: "search",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { denominator: "keywordVolume" },
    compatibleWidgets: ["kpi", "bar-chart", "data-table"],
    apiField: "cpc",
    description:
      "Per-keyword USD cost-per-click. Wire field: `cpc` (Keywords Explorer) / `cpc` (Organic Keywords) / `cost_per_click` (Rank Tracker per-row). Dual-naming rule per AC #14.",
  },
  {
    key: "clicksPerSearch",
    label: "Clicks Per Search",
    type: "number",
    category: "search",
    dataType: "Decimal",
    aggregationKind: "ratio", ratioComponents: { numerator: "keywordsExplorerClicks", denominator: "keywordVolume" },
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "cps",
    description:
      "Avg organic+paid clicks per 100 searches. Wire field: `cps` (Keywords Explorer) / `clicks_per_search` (Rank Tracker per-row). Dual-naming rule per AC #14.",
  },
  {
    key: "keywordDifficulty",
    label: "Keyword Difficulty",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi", "gauge", "data-table"],
    // Source endpoint is Keywords Explorer (`difficulty`); Organic Keywords
    // uses `keyword_difficulty` — the transformer emits the synthetic
    // `keywordDifficulty` mirror on both.
    apiField: "difficulty",
    description:
      "Ahrefs Keyword Difficulty 0-100. Wire field: `difficulty` (Keywords Explorer) / `keyword_difficulty` (Organic Keywords / Rank Tracker per-row). Dual-naming per AC #14.",
  },
  {
    key: "globalVolume",
    label: "Global Search Volume",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "global_volume",
    description: "Sum of monthly search volume across all countries.",
  },
  {
    key: "keywordIntents",
    label: "Keyword Intents",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "intents",
    description:
      "Boolean object — `{ informational, navigational, commercial, transactional, branded, local }` flags from Ahrefs.",
  },
  {
    key: "parentTopic",
    label: "Parent Topic",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "parent_topic",
    description: "The parent keyword that aggregates traffic across related variants.",
  },
  {
    key: "parentVolume",
    label: "Parent Topic Volume",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "parent_volume",
    description: "Search volume of the parent topic — useful for content-cluster sizing.",
  },
  {
    key: "searchesPctClicksOrganicAndPaid",
    label: "% Clicks (Organic + Paid)",
    type: "percentage",
    category: "search",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "keywordVolume" },
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "searches_pct_clicks_organic_and_paid",
    description: "Share of total searches that result in any click.",
  },
  {
    key: "searchesPctClicksOrganicOnly",
    label: "% Clicks (Organic Only)",
    type: "percentage",
    category: "search",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "keywordVolume" },
    compatibleWidgets: ["kpi"],
    apiField: "searches_pct_clicks_organic_only",
    description: "Share of total searches that click an organic result.",
  },
  {
    key: "searchesPctClicksPaidOnly",
    label: "% Clicks (Paid Only)",
    type: "percentage",
    category: "search",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "keywordVolume" },
    compatibleWidgets: ["kpi"],
    apiField: "searches_pct_clicks_paid_only",
    description: "Share of total searches that click a paid ad.",
  },
  {
    key: "serpFeaturesArray",
    label: "SERP Features",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "serp_features",
    description: "Array of SERP features present (AI Overview, Featured Snippet, Knowledge Panel, etc.).",
  },
  {
    key: "serpLastUpdate",
    label: "SERP Last Update",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "serp_last_update",
    description: "ISO timestamp of when Ahrefs last refreshed the SERP for this keyword.",
  },
  {
    key: "trafficPotential",
    label: "Traffic Potential",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "traffic_potential",
    description:
      "Estimated traffic the #1 ranking page receives across all keywords it ranks for — Ahrefs' canonical content-strategy metric.",
  },
  {
    key: "keywordVolume",
    label: "Search Volume",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    apiField: "volume",
    description: "12-month avg monthly searches in the queried country.",
  },
  {
    key: "volumeDesktopPct",
    label: "Volume (Desktop %)",
    type: "percentage",
    category: "search",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "keywordVolume" },
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "volume_desktop_pct",
    description: "Share of search volume coming from desktop devices.",
  },
  {
    key: "volumeMobilePct",
    label: "Volume (Mobile %)",
    type: "percentage",
    category: "search",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "keywordVolume" },
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "volume_mobile_pct",
    description: "Share of search volume coming from mobile devices.",
  },

  // ==========================================================================
  // Story 20.2 — Organic Keywords (+12 metrics) [AC #1, #16]
  // ==========================================================================
  {
    key: "bestPosition",
    label: "Best Position",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi", "data-table"],
    apiField: "best_position",
    description: "The single highest position the target ranks at for this keyword.",
  },
  {
    key: "bestPositionDiff",
    label: "Position Change vs. Comparison Date",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "ratio",
    compatibleWidgets: ["data-table", "bar-chart"],
    apiField: "best_position_diff",
    description:
      "**Headline metric for Story 20.5 widget UI** — change in `best_position` since the comparison date. Negative = lost positions, positive = gained.",
  },
  {
    key: "organicSumTraffic",
    label: "Organic Sum Traffic (per keyword)",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table", "bar-chart"],
    apiField: "sum_traffic",
    description: "Estimated organic traffic the target receives from this keyword across all ranking pages.",
  },
  {
    key: "organicSumPaidTraffic",
    label: "Paid Sum Traffic (per keyword)",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table"],
    apiField: "sum_paid_traffic",
    description: "Estimated paid traffic the target receives from this keyword.",
  },
  {
    key: "organicKeywordVolume",
    label: "Organic Keyword Volume",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table", "bar-chart"],
    apiField: "volume",
    description: "Search volume for the keyword in the per-row country.",
  },
  {
    key: "isCommercial",
    label: "Commercial Intent Flag",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "is_commercial",
    description: "Boolean flag — keyword has commercial intent.",
  },
  {
    key: "isInformational",
    label: "Informational Intent Flag",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "is_informational",
    description: "Boolean flag — keyword has informational intent.",
  },
  {
    key: "serpFeaturesCount",
    label: "SERP Features Count (per keyword)",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table"],
    apiField: "serp_features_count",
    description: "Total number of distinct SERP features present on the keyword's results page.",
  },
  {
    key: "organicVolumeDesktopPct",
    label: "Organic Volume (Desktop %)",
    type: "percentage",
    category: "search",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "organicKeywordVolume" },
    compatibleWidgets: ["kpi", "gauge", "data-table"],
    apiField: "volume_desktop_pct",
    description: "Per-keyword share of volume from desktop (Organic Keywords endpoint).",
  },
  {
    key: "organicVolumeMobilePct",
    label: "Organic Volume (Mobile %)",
    type: "percentage",
    category: "search",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "organicKeywordVolume" },
    compatibleWidgets: ["kpi", "gauge", "data-table"],
    apiField: "volume_mobile_pct",
    description: "Per-keyword share of volume from mobile (Organic Keywords endpoint).",
  },
  {
    key: "organicCpc",
    label: "Organic CPC (per keyword)",
    type: "currency",
    category: "search",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { denominator: "organicKeywordVolume" },
    compatibleWidgets: ["kpi", "data-table"],
    apiField: "cpc",
    description:
      "Per-keyword CPC from Organic Keywords endpoint. Wire-format `cpc` (cents); transformer divides by 100.",
  },
  {
    key: "organicKeywordDifficulty",
    label: "Keyword Difficulty (per keyword)",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi", "gauge", "data-table"],
    apiField: "keyword_difficulty",
    description:
      "Per-keyword difficulty from Organic Keywords endpoint. Mirrors Keywords Explorer's `difficulty` via the dual-naming rule.",
  },

  // ==========================================================================
  // Story 20.2 — Rank Tracker Competitors Stats (+15 metrics) [AC #1, #16]
  // ==========================================================================
  {
    key: "shareOfVoice",
    label: "Share of Voice",
    type: "percentage",
    category: "search",
    dataType: "Ratio",
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "share_of_voice",
    description:
      "Tracked-keyword Share of Voice (0-1 ratio per docs — NOT a percentage). Sum of competitor visibility weighted by position.",
  },
  {
    key: "shareOfTrafficValue",
    label: "Share of Traffic Value",
    type: "percentage",
    category: "search",
    dataType: "Ratio",
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "share_of_traffic_value",
    description:
      "Share of total tracked-keyword traffic value attributable to this competitor (0-1 ratio — NOT cents).",
  },
  {
    key: "rankTrackerTraffic",
    label: "Tracked Keywords Traffic",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "traffic",
    description: "Estimated traffic across all tracked keywords for this competitor.",
  },
  {
    key: "rankTrackerTrafficValue",
    label: "Tracked Keywords Traffic Value",
    type: "currency",
    category: "search",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "traffic_value",
    description: "USD value of equivalent paid traffic for tracked keywords (cents → dollars).",
  },
  {
    key: "pos1_3",
    label: "Positions 1-3",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "pos_1_3",
    description: "Number of tracked keywords ranking in positions 1-3.",
  },
  {
    key: "pos4_10",
    label: "Positions 4-10",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "pos_4_10",
    description: "Number of tracked keywords ranking in positions 4-10.",
  },
  {
    key: "pos11_20",
    label: "Positions 11-20",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "pos_11_20",
    description: "Number of tracked keywords ranking in positions 11-20.",
  },
  {
    key: "pos21_50",
    label: "Positions 21-50",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "pos_21_50",
    description: "Number of tracked keywords ranking in positions 21-50.",
  },
  {
    key: "pos51_plus",
    label: "Positions 51+",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "pos_51_plus",
    description: "Number of tracked keywords ranking beyond position 50.",
  },
  {
    key: "posNoRank",
    label: "Not Ranking",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "pos_no_rank",
    description: "Number of tracked keywords where the competitor does not rank in the top 100.",
  },
  {
    key: "featuredSnippetCount",
    label: "Featured Snippet Count",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "featured_snippet_count",
    description: "Tracked keywords where the competitor owns the Featured Snippet.",
  },
  {
    key: "knowledgePanelCount",
    label: "Knowledge Panel Count",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "knowledge_panel_count",
    description: "Tracked keywords where a Knowledge Panel is present.",
  },
  {
    key: "sitelinksCount",
    label: "Sitelinks Count",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "sitelinks_count",
    description: "Tracked keywords where the competitor's result includes Sitelinks.",
  },
  {
    key: "topStoriesCount",
    label: "Top Stories Count",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "top_stories_count",
    description: "Tracked keywords where Top Stories is present in the SERP.",
  },
  {
    key: "aiOverviewCount",
    label: "AI Overview Count",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "ai_overview_count",
    description: "Tracked keywords where Google's AI Overview is present in the SERP.",
  },

  // ==========================================================================
  // Story 20.2 — Keywords History (+5 metrics) [AC #1, #16]
  // ==========================================================================
  {
    key: "top3",
    label: "Keywords in Top 3",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["line-chart", "bar-chart", "kpi"],
    apiField: "top3",
    description: "Time-series count of keywords ranking in positions 1-3 (Keywords History endpoint).",
  },
  {
    key: "top4_10",
    label: "Keywords in Top 4-10",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["line-chart", "bar-chart", "kpi"],
    apiField: "top4_10",
    description: "Time-series count of keywords ranking in positions 4-10.",
  },
  {
    key: "top11_20",
    label: "Keywords in Top 11-20",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["line-chart", "bar-chart", "kpi"],
    apiField: "top11_20",
    description: "Time-series count of keywords ranking in positions 11-20 (2026 expanded ranking group).",
  },
  {
    key: "top21_50",
    label: "Keywords in Top 21-50",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["line-chart", "bar-chart", "kpi"],
    apiField: "top21_50",
    description: "Time-series count of keywords ranking in positions 21-50 (2026 expanded ranking group).",
  },
  {
    key: "top51_plus",
    label: "Keywords in Top 51+",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["line-chart", "bar-chart", "kpi"],
    apiField: "top51_plus",
    description: "Time-series count of keywords ranking beyond position 50 (2026 expanded ranking group).",
  },

  // ==========================================================================
  // Story 20.2a — Metrics History (+4 metric fields) [AC #1]
  // ==========================================================================
  // The endpoint exposes 5 columns total per docs.ahrefs.com (verified
  // 2026-05-03): `date` (row marker, not a metric) + 4 metric fields below.
  {
    key: "organicTrafficHistory",
    label: "Organic Traffic (history)",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["line-chart", "kpi"],
    apiField: "org_traffic",
    description:
      "Time-series of estimated monthly organic traffic from /site-explorer/metrics-history. Default 12-month/monthly grouping in autonomous sync; 24-month range supported via widget config.",
  },
  {
    key: "organicTrafficValueHistory",
    label: "Organic Traffic Value (history)",
    type: "currency",
    category: "search",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["line-chart", "kpi"],
    apiField: "org_cost",
    description:
      "Time-series of equivalent paid USD value of organic traffic. Wire field `org_cost` returned as cents; transformer divides by 100.",
  },
  {
    key: "paidTrafficHistory",
    label: "Paid Traffic (history)",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["line-chart", "kpi"],
    apiField: "paid_traffic",
    description:
      "Time-series of estimated monthly paid search traffic from /site-explorer/metrics-history.",
  },
  {
    key: "paidTrafficCostHistory",
    label: "Paid Traffic Cost (history)",
    type: "currency",
    category: "search",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["line-chart", "kpi"],
    apiField: "paid_cost",
    description:
      "Time-series of estimated paid search spend USD. Wire field `paid_cost` returned as cents; transformer divides by 100.",
  },

  // ==========================================================================
  // Story 20.2a — Metrics by Country (+8 metric fields) [AC #1]
  // ==========================================================================
  // The endpoint exposes 9 columns total per docs.ahrefs.com (verified
  // 2026-05-03): `country` (dimension, not a metric) + 8 metric fields below.
  // `country` is added to AHREFS_DIMENSIONS already, so it does not appear
  // here. NOTE: `org_traffic` / `paid_traffic` / `org_cost` / `paid_cost` are
  // already declared above (in Story 20.1's metrics-snapshot block); the
  // metrics-by-country versions get distinct `key` values to keep widget UI
  // selectors unambiguous (a per-country breakdown is conceptually different
  // from a single rolled-up snapshot).
  {
    key: "organicTrafficByCountry",
    label: "Organic Traffic (by country)",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table", "bar-chart"],
    apiField: "org_traffic",
    description: "Per-country breakdown of estimated monthly organic traffic.",
  },
  {
    key: "organicTrafficValueByCountry",
    label: "Organic Traffic Value (by country)",
    type: "currency",
    category: "search",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table", "bar-chart"],
    apiField: "org_cost",
    description:
      "Per-country breakdown of organic-traffic dollar value. Wire field `org_cost` returned as cents; transformer divides by 100.",
  },
  {
    key: "organicKeywordsByCountry",
    label: "Organic Keywords (by country)",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table", "bar-chart"],
    apiField: "org_keywords",
    description: "Per-country count of keywords the target ranks for organically.",
  },
  {
    key: "organicKeywordsTop3ByCountry",
    label: "Organic Keywords Top 3 (by country)",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table", "bar-chart"],
    apiField: "org_keywords_1_3",
    description: "Per-country count of organic keywords ranking in positions 1-3.",
  },
  {
    key: "paidTrafficByCountry",
    label: "Paid Traffic (by country)",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table", "bar-chart"],
    apiField: "paid_traffic",
    description: "Per-country breakdown of estimated monthly paid search traffic.",
  },
  {
    key: "paidTrafficCostByCountry",
    label: "Paid Traffic Cost (by country)",
    type: "currency",
    category: "search",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table", "bar-chart"],
    apiField: "paid_cost",
    description:
      "Per-country breakdown of estimated paid search spend USD. Wire field `paid_cost` returned as cents; transformer divides by 100.",
  },
  {
    key: "paidKeywordsByCountry",
    label: "Paid Keywords (by country)",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table", "bar-chart"],
    apiField: "paid_keywords",
    description: "Per-country count of keywords the target bids on in paid search.",
  },
  {
    key: "paidPagesByCountry",
    label: "Paid Pages (by country)",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table", "bar-chart"],
    apiField: "paid_pages",
    description: "Per-country count of distinct landing pages receiving paid search traffic.",
  },

  // ==========================================================================
  // Story 20.2c — Rank Tracker per-keyword `/overview` (+29 wire + 2 synthetic)
  // ==========================================================================
  // FREE endpoint per docs.ahrefs.com/api/reference/rank-tracker/get-overview
  // (verified 2026-05-03). Per-keyword surface — sibling to the per-project
  // aggregate `/competitors-stats` (Story 20.2). Headline metrics for the
  // Rank Tracker widget UI (Story 20.3): `positionDiff` + `positionMomentum`
  // (signed: positive = moved up = green chevron) + `appearedToday`.
  //
  // Dual-naming overlap NOTE: keyword/keyword_difficulty/cost_per_click/
  // clicks_per_search/clicks/volume/volume_desktop_pct/volume_mobile_pct/
  // serp_features/parent_topic/is_commercial/is_informational already exist
  // in AHREFS_METRICS via Keywords Explorer / Organic Keywords entries —
  // their camelCase mirrors are emitted by `transformAhrefsRankTrackerOverviewRow`
  // (Story 20.2 dual-naming rule, AC #14). NO duplicate metric entries.
  {
    key: "bestPositionHasThumbnail",
    label: "Best Position Has Thumbnail",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "best_position_has_thumbnail",
    description: "Whether the keyword's best ranking SERP result includes a thumbnail (Rank Tracker per-keyword).",
  },
  {
    key: "bestPositionHasVideoPreview",
    label: "Best Position Has Video Preview",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "best_position_has_video_preview",
    description: "Whether the keyword's best ranking SERP result includes a video preview (Rank Tracker per-keyword).",
  },
  {
    key: "bestPositionKind",
    label: "Best Position Kind",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "best_position_kind",
    description:
      "SERP-feature kind of the keyword's best ranking position (organic, ai_overview, featured_snippet, sitelink, knowledge_card, etc.) — Rank Tracker per-keyword.",
  },
  {
    key: "position",
    label: "Position",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi", "data-table"],
    apiField: "position",
    description: "Per-keyword SERP position on the snapshot date (Rank Tracker `/overview`). Lower is better.",
  },
  {
    key: "positionPrev",
    label: "Position (Compared Date)",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "ratio",
    compatibleWidgets: ["data-table"],
    apiField: "position_prev",
    description:
      "Keyword's position on the `date_compared` snapshot. Used together with `position` to compute `positionMomentum`.",
  },
  {
    key: "positionDiff",
    label: "Position Change",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "ratio",
    compatibleWidgets: ["data-table", "kpi", "bar-chart"],
    apiField: "position_diff",
    description:
      "**Headline metric for the Rank Tracker widget UI** — wire-format `position_diff` (negative-is-good per Ahrefs convention because lower position is better). The synthetic `positionMomentum` is the inverse-sign companion (positive = moved up).",
  },
  {
    key: "isMainPosition",
    label: "Is Main Position",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "is_main_position",
    description:
      "Boolean — flags the canonical row when a keyword has multiple ranking URLs (e.g. www.example.com/ AND example.com/). Use as a `WHERE is_main_position` filter to avoid double-counting traffic.",
  },
  {
    key: "targetPositionsCount",
    label: "Target Positions Count",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table"],
    apiField: "target_positions_count",
    description: "Number of distinct ranking positions the target holds for this keyword.",
  },
  {
    key: "keywordHasData",
    label: "Keyword Has Data",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "keyword_has_data",
    description:
      "Boolean — `true` when Ahrefs has fresh ranking data for the keyword on the snapshot date. `false` when not yet crawled.",
  },
  {
    key: "keywordIsFrozen",
    label: "Keyword Is Frozen",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "keyword_is_frozen",
    description: "Boolean — `true` when the customer has paused tracking for this keyword. Frozen keywords have stale ranking data.",
  },
  {
    key: "keywordWords",
    label: "Keyword Word Count",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table", "kpi"],
    apiField: "keyword_words",
    description: "Number of words in the keyword. Useful for segmenting head terms vs long-tail.",
  },
  {
    key: "rankTrackerKeywordTraffic",
    label: "Tracked Keyword Traffic",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table", "kpi", "bar-chart"],
    apiField: "traffic",
    description:
      "Per-keyword estimated traffic on the Rank Tracker snapshot date. Distinct from `rankTrackerTraffic` which is the per-competitor aggregate from `/competitors-stats`.",
  },
  {
    key: "rankTrackerKeywordTrafficPrev",
    label: "Tracked Keyword Traffic (Compared)",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "traffic_prev",
    description: "Per-keyword traffic on the `date_compared` snapshot.",
  },
  {
    key: "rankTrackerKeywordTrafficDiff",
    label: "Tracked Keyword Traffic Change",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table", "kpi", "bar-chart"],
    apiField: "traffic_diff",
    description: "Per-keyword traffic delta vs the compared date (positive = traffic gained).",
  },
  {
    key: "serpFeaturesPrev",
    label: "SERP Features (Compared)",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "serp_features_prev",
    description: "Array of SERP features present on the `date_compared` snapshot.",
  },
  {
    key: "rankTrackerCountry",
    label: "Tracked Keyword Country",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "country",
    description: "Per-keyword ISO country code (Rank Tracker per-keyword surface).",
  },
  {
    key: "rankTrackerLocation",
    label: "Tracked Keyword Location",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "location",
    description: "Per-keyword location string (city / region scope), where configured.",
  },
  {
    key: "rankTrackerLanguage",
    label: "Tracked Keyword Language",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "language",
    description: "Per-keyword ISO language code (Rank Tracker per-keyword surface).",
  },
  {
    key: "searchTypeWeb",
    label: "Search Type — Web",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table", "bar-chart"],
    apiField: "search_type_web",
    description: "Count of SERP results scanned in the Web search type for this keyword's position measurement.",
  },
  {
    key: "searchTypeImage",
    label: "Search Type — Image",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table", "bar-chart"],
    apiField: "search_type_image",
    description: "Count of SERP results scanned in the Image search type for this keyword's position measurement.",
  },
  {
    key: "searchTypeNews",
    label: "Search Type — News",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table", "bar-chart"],
    apiField: "search_type_news",
    description: "Count of SERP results scanned in the News search type for this keyword's position measurement.",
  },
  {
    key: "searchTypeVideo",
    label: "Search Type — Video",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table", "bar-chart"],
    apiField: "search_type_video",
    description: "Count of SERP results scanned in the Video search type for this keyword's position measurement.",
  },
  {
    key: "isBranded",
    label: "Branded Intent Flag",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "is_branded",
    description: "Boolean — keyword has branded intent (references the customer's or competitor's brand name). Added by Ahrefs 2025-07-02.",
  },
  {
    key: "isLocal",
    label: "Local Intent Flag",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "is_local",
    description: "Boolean — keyword has local intent (e.g. \"near me\", city-modified queries). Added by Ahrefs 2025-07-02.",
  },
  {
    key: "isNavigational",
    label: "Navigational Intent Flag",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "is_navigational",
    description: "Boolean — keyword has navigational intent (user is looking for a specific website). Added by Ahrefs 2025-07-02.",
  },
  {
    key: "isTransactional",
    label: "Transactional Intent Flag",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "is_transactional",
    description: "Boolean — keyword has transactional intent (purchase-ready queries). Added by Ahrefs 2025-07-02.",
  },
  {
    key: "keywordCreatedAt",
    label: "Keyword Created At",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "created_at",
    description: "ISO timestamp — when the keyword was added to the Rank Tracker project.",
  },
  {
    key: "serpUpdated",
    label: "SERP Last Updated",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "serp_updated",
    description: "ISO timestamp — when Ahrefs last refreshed the SERP for this keyword in the Rank Tracker project.",
  },
  {
    key: "keywordTags",
    label: "Keyword Tags",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "tags",
    description:
      "Array of customer-applied classification tags (e.g. \"branded\", \"non-branded\", \"competitor\"). Tag-based filtering uses the `where=tags=eq.<tag>` Ahrefs filter expression — `tags` is NOT a query parameter on this endpoint.",
  },

  // Story 20.2c — synthetic NaN-safe derived metrics (per AC #11).
  // No `apiField` — registered in AHREFS_SYNTHETIC_METRIC_KEYS so the
  // metric-allowlist tests exempt them from the apiField requirement.
  {
    key: "positionMomentum",
    label: "Position Momentum",
    type: "number",
    category: "search",
    dataType: "Integer",
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi", "data-table"],
    description:
      "Synthetic — `position_prev - position`. Signed integer where positive = \"moved up\" (lower numerical position is better in SERP terms). Null on missing/non-finite inputs or when `keyword_has_data === false`. **Headline metric for the Story 20.3 widget UI** — surfaced in the leftmost column with red ↓ / green ↑ chevrons.",
  },
  {
    key: "appearedToday",
    label: "Appeared Today",
    type: "number",
    category: "search",
    dataType: "Enum",
    compatibleWidgets: ["kpi", "data-table"],
    description:
      "Synthetic — `true` when the keyword newly appeared on `date` after being absent on `date_compared` (or fresh data with no compared date). False when the keyword is frozen or has no fresh data. Conservative semantics: `false` (not null) on missing data so widget badges have a deterministic value.",
  },

  // ==========================================================================
  // Story 20.2d — Site Audit /projects (+12 wire fields, FREE endpoint)
  // ==========================================================================
  // FREE per [docs.ahrefs.com/api/reference/site-audit/get-projects]
  // (verified 2026-05-05, "Requests to this endpoint are free and do not
  // consume any API units"). Headline metric for the Story 20.3 widget UI:
  // `health_score` (0-100 integer) — "Site Health: 78/100" agency-facing gauge.
  {
    key: "siteAuditProjectId",
    label: "Site Audit Project ID",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "project_id",
    description: "Ahrefs Site Audit project identifier (string). Required by /issues, /page-explorer, /page-content callers.",
  },
  {
    key: "siteAuditProjectName",
    label: "Site Audit Project Name",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "project_name",
    description: "Customer-supplied project label in the Ahrefs Site Audit UI.",
  },
  {
    key: "siteAuditTargetProtocol",
    label: "Site Audit Target Protocol",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "target_protocol",
    description: "Configured protocol for the audit target — `http` or `https`.",
  },
  {
    key: "siteAuditTargetUrl",
    label: "Site Audit Target URL",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "target_url",
    description: "Configured target URL/domain for the audit project.",
  },
  {
    key: "siteAuditTargetMode",
    label: "Site Audit Target Mode",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "target_mode",
    description: "Audit target scope — `domain`, `subdomain`, `prefix`, or `exact_url`.",
  },
  {
    key: "siteAuditDate",
    label: "Site Audit Crawl Date",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "date",
    description: "ISO timestamp of the most-recent crawl returned for this project row.",
  },
  {
    key: "siteAuditStatus",
    label: "Site Audit Crawl Status",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "status",
    description: "Crawl status string — null while a crawl is in progress.",
  },
  {
    key: "healthScore",
    label: "Site Health Score",
    type: "percentage",
    category: "score",
    dataType: "Percentage",
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "health_score",
    description:
      "**Headline Site Audit metric for the Story 20.3 widget UI** — 0-100 health score. Threshold rendering: red (<60) / yellow (60-79) / green (≥80). Null while a crawl is in progress.",
  },
  {
    key: "urlsWithErrors",
    label: "URLs With Errors",
    type: "number",
    category: "score",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "urls_with_errors",
    description: "Count of crawled URLs flagged with at least one Error-level Site Audit issue.",
  },
  {
    key: "urlsWithWarnings",
    label: "URLs With Warnings",
    type: "number",
    category: "score",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "urls_with_warnings",
    description: "Count of crawled URLs flagged with at least one Warning-level Site Audit issue.",
  },
  {
    key: "urlsWithNotices",
    label: "URLs With Notices",
    type: "number",
    category: "score",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "urls_with_notices",
    description: "Count of crawled URLs flagged with at least one Notice-level Site Audit issue.",
  },
  {
    key: "siteAuditTotalUrls",
    label: "Total URLs Crawled",
    type: "number",
    category: "score",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "total",
    description: "Total URLs crawled in this Site Audit project run.",
  },

  // ==========================================================================
  // Story 20.2d — Site Audit /issues (+11 wire fields, paid 50u/request fixed)
  // ==========================================================================
  // Per [docs.ahrefs.com/api/reference/site-audit/get-issues] (verified
  // 2026-05-05). Cost is fixed 50 units/request (NOT row-scaled).
  //
  // `importance` ∈ `Error | Warning | Notice` (capitalized literal, NOT
  // `severity`). `category` is a 15-value enum (Internal pages, Indexability,
  // Links, Redirects, Content, Social tags, Duplicates, Localization,
  // Usability and performance, Images, JavaScript, CSS, Sitemaps, External
  // pages, Other).
  {
    key: "issueId",
    label: "Site Audit Issue ID",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "issue_id",
    description: "Stable Ahrefs identifier for the Site Audit issue.",
  },
  {
    key: "siteAuditIssueName",
    label: "Site Audit Issue Name",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "name",
    description: "Human-readable issue name (e.g. \"Broken external link\", \"Missing canonical\").",
  },
  {
    key: "siteAuditIssueImportance",
    label: "Site Audit Issue Importance",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table", "kpi", "bar-chart"],
    apiField: "importance",
    description: "Severity bucket (`Error` / `Warning` / `Notice`). Wire-format enum is capitalized.",
  },
  {
    key: "siteAuditIssueCategory",
    label: "Site Audit Issue Category",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table", "bar-chart"],
    apiField: "category",
    description:
      "Issue category (15 values: Internal pages / Indexability / Links / Redirects / Content / Social tags / Duplicates / Localization / Usability and performance / Images / JavaScript / CSS / Sitemaps / External pages / Other).",
  },
  {
    key: "siteAuditIssueIsIndexable",
    label: "Site Audit Issue Is Indexable",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "is_indexable",
    description: "Boolean — whether issue affects indexable pages (per Ahrefs canonical/robots evaluation).",
  },
  {
    key: "siteAuditIssueCrawled",
    label: "Site Audit Issue Crawled Count",
    type: "number",
    category: "score",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table", "kpi", "bar-chart"],
    apiField: "crawled",
    description: "Count of URLs flagged by this issue on the snapshot crawl.",
  },
  {
    key: "siteAuditIssueChange",
    label: "Site Audit Issue Change",
    type: "number",
    category: "score",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table", "kpi"],
    apiField: "change",
    description: "Net change vs `date_compared` snapshot. Null without comparison date.",
  },
  {
    key: "siteAuditIssueAdded",
    label: "Site Audit Issue Added",
    type: "number",
    category: "score",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "added",
    description: "URLs newly affected by this issue since `date_compared`. Null without comparison date.",
  },
  {
    key: "siteAuditIssueNew",
    label: "Site Audit Issue Newly-Detected",
    type: "number",
    category: "score",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "new",
    description: "URLs where the issue is newly detected (vs already affected on `date_compared`). Null without comparison date.",
  },
  {
    key: "siteAuditIssueRemoved",
    label: "Site Audit Issue Removed",
    type: "number",
    category: "score",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "removed",
    description: "URLs no longer affected by this issue since `date_compared`. Null without comparison date.",
  },
  {
    key: "siteAuditIssueMissing",
    label: "Site Audit Issue Missing",
    type: "number",
    category: "score",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "missing",
    description: "URLs that were present on `date_compared` but missing from the current crawl. Null without comparison date.",
  },

  // ==========================================================================
  // Story 20.2e — Brand Radar /ai-responses (+7 wire fields)
  // ==========================================================================
  // Per [docs.ahrefs.com/api/reference/brand-radar/get-ai-responses] (verified
  // 2026-05-05). 3 fields carry per-field 10u surcharge (`response`, `links`,
  // `volume`); the other 4 are free. Default-select excludes the 3 paid fields.
  {
    key: "brandRadarCountry",
    label: "Brand Radar Country",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "country",
    description: "ISO 3166-1 alpha-2 country code for the AI response row (Brand Radar). Free field per docs.",
  },
  {
    key: "brandRadarQuestion",
    label: "Brand Radar Question",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "question",
    description: "User question that produced the AI response — surfaces the underlying SERP query Brand Radar tracked.",
  },
  {
    key: "brandRadarResponse",
    label: "Brand Radar Response",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "response",
    description: "Verbatim AI-generated answer text. **Paid 10u/included field** — opt-in via widget config to control unit-budget burn.",
  },
  {
    key: "brandRadarLinks",
    label: "Brand Radar Cited Links",
    type: "number",
    category: "links",
    dataType: "Array",
    compatibleWidgets: ["data-table"],
    apiField: "links",
    description: "Array of cited URLs in the AI response. **Paid 10u/included field**.",
  },
  {
    key: "brandRadarSearchQueries",
    label: "Brand Radar Search Queries",
    type: "number",
    category: "score",
    dataType: "Array",
    compatibleWidgets: ["data-table"],
    apiField: "search_queries",
    description: "Array of refined search queries (ChatGPT/Perplexity only). Free field per docs.",
  },
  {
    key: "brandRadarTags",
    label: "Brand Radar Topical Tags",
    type: "number",
    category: "score",
    dataType: "Array",
    compatibleWidgets: ["data-table"],
    apiField: "tags",
    description: "Ahrefs-assigned topical tags array (e.g. `health`, `wellness`). Free field per docs.",
  },
  {
    key: "brandRadarVolume",
    label: "Brand Radar Search Volume",
    type: "number",
    category: "traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table", "kpi"],
    apiField: "volume",
    description: "Search-volume estimate for the underlying query. **Paid 10u/included field** on /ai-responses (and DEPRECATED on /cited-* endpoints — see brandRadarCitedDomainsVolume).",
  },

  // ==========================================================================
  // Story 20.2e — Brand Radar /impressions-history + /mentions-history (+4 wire fields)
  // ==========================================================================
  {
    key: "brandRadarImpressionsHistoryDate",
    label: "Brand Radar Impressions History Date",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table", "line-chart"],
    apiField: "date",
    description: "Time-bucket date for impression count rows on /impressions-history. YYYY-MM-DD.",
  },
  {
    key: "brandRadarImpressions",
    label: "Brand Radar Impressions",
    type: "number",
    category: "traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    apiField: "impressions",
    description: "Per-day impression count across the requested AI clients. Time-series companion to brandRadarImpressionsOverviewTotal.",
  },
  {
    key: "brandRadarMentionsHistoryDate",
    label: "Brand Radar Mentions History Date",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table", "line-chart"],
    apiField: "date",
    description: "Time-bucket date for mention count rows on /mentions-history. YYYY-MM-DD.",
  },
  {
    key: "brandRadarMentions",
    label: "Brand Radar Mentions",
    type: "number",
    category: "traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    // The `apiField` here is the legitimate count field on /mentions-history
    // (NOT the deprecated cited-* field). The metric-allowlist guardrail is
    // narrowed to context-aware per AC #5.
    apiField: "mentions",
    description: "Per-day mention count across the requested AI clients. Distinct from the deprecated `mentions` field on /cited-domains and /cited-pages — this is the legitimate count metric.",
  },

  // ==========================================================================
  // Story 20.2e — Brand Radar /impressions-overview (+6 wire fields)
  // ==========================================================================
  // Per [docs.ahrefs.com/api/reference/brand-radar/get-impressions-overview]
  // (verified 2026-05-05). 5-bucket integer shape — agencies render as a
  // stacked bar chart of "Only target brand / Only competitors / Target +
  // competitors / No tracked brands / Total".
  {
    key: "brandRadarImpressionsOverviewBrand",
    label: "Brand Radar Impressions Brand",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "brand",
    description: "Tracked brand name for the impressions-overview row.",
  },
  {
    key: "brandRadarImpressionsOnlyTargetBrand",
    label: "Brand Radar Impressions — Only Target Brand",
    type: "number",
    category: "traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "only_target_brand",
    description: "Impressions where ONLY the target brand was cited (no competitor brands). 5-bucket stacked-bar input.",
  },
  {
    key: "brandRadarImpressionsOnlyCompetitorsBrands",
    label: "Brand Radar Impressions — Only Competitors",
    type: "number",
    category: "traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "only_competitors_brands",
    description: "Impressions where ONLY competitor brands were cited (target brand missing). 5-bucket stacked-bar input.",
  },
  {
    key: "brandRadarImpressionsTargetAndCompetitors",
    label: "Brand Radar Impressions — Target + Competitors",
    type: "number",
    category: "traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "target_and_competitors_brands",
    description: "Impressions where BOTH the target brand AND at least one competitor were cited. 5-bucket stacked-bar input.",
  },
  {
    key: "brandRadarImpressionsNoTrackedBrands",
    label: "Brand Radar Impressions — No Tracked Brands",
    type: "number",
    category: "traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "no_tracked_brands",
    description: "Impressions where no tracked brands were cited (white-space). 5-bucket stacked-bar input.",
  },
  {
    key: "brandRadarImpressionsTotal",
    label: "Brand Radar Impressions — Total",
    type: "number",
    category: "traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "total",
    description: "Total impression count across all 4 brand-citation buckets. Headline KPI for impressions-overview.",
  },

  // ==========================================================================
  // Story 20.2e — Brand Radar /mentions-overview (+6 wire fields)
  // ==========================================================================
  // Same 6-field shape as /impressions-overview but counts brand MENTIONS in
  // AI responses (not impression-share).
  {
    key: "brandRadarMentionsOverviewBrand",
    label: "Brand Radar Mentions Brand",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "brand",
    description: "Tracked brand name for the mentions-overview row.",
  },
  {
    key: "brandRadarMentionsOnlyTargetBrand",
    label: "Brand Radar Mentions — Only Target Brand",
    type: "number",
    category: "traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "only_target_brand",
    description: "Mentions where ONLY the target brand was cited (no competitors).",
  },
  {
    key: "brandRadarMentionsOnlyCompetitorsBrands",
    label: "Brand Radar Mentions — Only Competitors",
    type: "number",
    category: "traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "only_competitors_brands",
    description: "Mentions where ONLY competitor brands were cited (target brand missing).",
  },
  {
    key: "brandRadarMentionsTargetAndCompetitors",
    label: "Brand Radar Mentions — Target + Competitors",
    type: "number",
    category: "traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "target_and_competitors_brands",
    description: "Mentions where BOTH the target brand AND at least one competitor were cited.",
  },
  {
    key: "brandRadarMentionsNoTrackedBrands",
    label: "Brand Radar Mentions — No Tracked Brands",
    type: "number",
    category: "traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "no_tracked_brands",
    description: "Mentions where no tracked brands were cited (white-space).",
  },
  {
    key: "brandRadarMentionsTotal",
    label: "Brand Radar Mentions — Total",
    type: "number",
    category: "traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "total",
    description: "Total mention count across all 4 brand-citation buckets. Headline KPI for mentions-overview.",
  },

  // ==========================================================================
  // Story 20.2e — Brand Radar /sov-overview (+2 wire fields)
  // ==========================================================================
  // The headline agency-facing AI Share-of-Voice gauge (Story 20.3 widget UI).
  {
    key: "brandRadarSovBrand",
    label: "Brand Radar SoV Brand",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "brand",
    description: "Tracked brand name for the share-of-voice row.",
  },
  {
    key: "brandRadarShareOfVoice",
    label: "Brand Radar Share of Voice",
    type: "percentage",
    category: "score",
    dataType: "Ratio",
    aggregationKind: "ratio", ratioComponents: { denominator: "brandRadarImpressionsTotal" },
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "share_of_voice",
    description:
      "**Headline AI visibility metric.** Float 0-1 representing share of brand mentions across the requested AI clients on `/sov-overview`. Story 20.3 widget renders as percentage with green/yellow/red threshold bands. **Type-disambiguation note:** `apiField: \"share_of_voice\"` ALSO appears on `/sov-history` rows (`brandRadarSovHistoryShareOfVoice`) but as a NESTED ARRAY `Array<{brand,value}>`. Consumers MUST switch on `_reportType` (`brand-radar-sov-overview` vs `brand-radar-sov-history`) before destructuring.",
  },

  // ==========================================================================
  // Story 20.2e — Brand Radar /sov-history (+2 wire fields)
  // ==========================================================================
  // Per-date row carries a NESTED `share_of_voice: Array<{brand, value}>` —
  // transformer preserves nested shape (catalogue row 209).
  {
    key: "brandRadarSovHistoryDate",
    label: "Brand Radar SoV History Date",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table", "line-chart"],
    apiField: "date",
    description: "Time-bucket date for share-of-voice rows on /sov-history. YYYY-MM-DD.",
  },
  {
    key: "brandRadarSovHistoryShareOfVoice",
    label: "Brand Radar SoV History — Per-Brand Share",
    type: "percentage",
    category: "score",
    dataType: "Array",
    compatibleWidgets: ["line-chart", "stacked-bar"],
    apiField: "share_of_voice",
    description:
      "Nested per-date array of `{brand, value}` objects (float 0-1 per brand). Widgets render as a stacked time-series with brand-as-color. **Type-disambiguation note:** `apiField: \"share_of_voice\"` ALSO appears on `/sov-overview` (`brandRadarShareOfVoice`) but as a SCALAR `Ratio` (not an array). Consumers MUST switch on `_reportType` (`brand-radar-sov-history` vs `brand-radar-sov-overview`) or `dataType` (`\"Array\"` vs `\"Ratio\"`) before destructuring.",
  },

  // ==========================================================================
  // Story 20.2e — Brand Radar /cited-domains (+5 wire fields, 2 deprecated)
  // ==========================================================================
  {
    key: "brandRadarCitedDomain",
    label: "Brand Radar Cited Domain",
    type: "number",
    category: "links",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "domain",
    description: "Domain cited in AI responses for the tracked brand topic. Sortable by `responses` count.",
  },
  {
    key: "brandRadarCitedDomainPages",
    label: "Brand Radar Cited Domain — Pages",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table", "kpi"],
    apiField: "pages",
    description: "Distinct page count from this domain that AI clients cited.",
  },
  {
    key: "brandRadarCitedDomainResponses",
    label: "Brand Radar Cited Domain — Responses",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table", "kpi"],
    apiField: "responses",
    description:
      "Count of AI responses that cited this domain. **Replacement for the deprecated `mentions` field** (deprecated 2026-02-10).",
  },
  {
    key: "brandRadarCitedDomainMentionsDeprecated",
    label: "Brand Radar Cited Domain — Mentions (DEPRECATED)",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    // The `apiField` here is the deprecated wire field. The transformer NULLs
    // it at ingest regardless of wire payload (per AC #5). Catalogued for
    // historical-widget compatibility — widgets binding to this key receive
    // null and render "no data" rather than stale values.
    apiField: "mentions",
    deprecated: true,
    replacedBy: "brandRadarCitedDomainResponses",
    description: "DEPRECATED 2026-02-10 (replaced by `responses` → `brandRadarCitedDomainResponses`). Stripped at ingest — always null. Catalogued for historical-widget compatibility.",
  },
  {
    key: "brandRadarCitedDomainVolumeDeprecated",
    label: "Brand Radar Cited Domain — Volume (DEPRECATED)",
    type: "number",
    category: "traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "volume",
    deprecated: true,
    replacedBy: null,
    description: "DEPRECATED 2026-03-24 (no replacement; metric retired). Stripped at ingest — always null. Catalogued for historical-widget compatibility.",
  },

  // ==========================================================================
  // Story 20.2e — Brand Radar /cited-pages (+4 wire fields, 2 deprecated)
  // ==========================================================================
  {
    key: "brandRadarCitedPageUrl",
    label: "Brand Radar Cited Page URL",
    type: "number",
    category: "links",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "url",
    description: "URL cited in AI responses. Use the `tracked_urls` request param to surface zero-citation rows for newly-published pages.",
  },
  {
    key: "brandRadarCitedPageResponses",
    label: "Brand Radar Cited Page — Responses",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table", "kpi"],
    apiField: "responses",
    description:
      "Count of AI responses that cited this URL. **Replacement for the deprecated `mentions` field** (deprecated 2026-02-10).",
  },
  {
    key: "brandRadarCitedPageMentionsDeprecated",
    label: "Brand Radar Cited Page — Mentions (DEPRECATED)",
    type: "number",
    category: "links",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "mentions",
    deprecated: true,
    replacedBy: "brandRadarCitedPageResponses",
    description: "DEPRECATED 2026-02-10 (replaced by `responses` → `brandRadarCitedPageResponses`). Stripped at ingest — always null.",
  },
  {
    key: "brandRadarCitedPageVolumeDeprecated",
    label: "Brand Radar Cited Page — Volume (DEPRECATED)",
    type: "number",
    category: "traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "volume",
    deprecated: true,
    replacedBy: null,
    description: "DEPRECATED 2026-03-24 (no replacement; metric retired). Stripped at ingest — always null.",
  },

  // ==========================================================================
  // Story 20.2f — Web Analytics /stats (+6 wire fields)
  // ==========================================================================
  // All 6 fields catalogued in `_bmad/bmm/data/integration-metrics/ahrefs.md`
  // rows 226-231. Each field is dual-tagged on `_reportType ∈
  // {"web-analytics-stats", "web-analytics-chart"}` per the dual-naming rule
  // from Story 20.2 (no duplicate definitions; the transformer emits the
  // `_reportType` discriminator on each row). Verified against
  // [docs.ahrefs.com/api/reference/web-analytics/get-stats] on 2026-05-06.
  {
    key: "webAnalyticsPageviews",
    label: "Web Analytics — Pageviews",
    type: "number",
    category: "traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "pageviews",
    description:
      "Total pageviews for the date range (cookieless tracking). Available on both `web-analytics-stats` (singleton aggregate) and `web-analytics-chart` (per-timestamp time-series).",
  },
  {
    key: "webAnalyticsVisitors",
    label: "Web Analytics — Visitors",
    type: "number",
    category: "audience",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "visitors",
    description:
      "Unique visitor count derived from `IP + user-agent + daily-rotated salt` (cookieless; salts deleted every 24h). Available on `web-analytics-stats` and `web-analytics-chart`.",
  },
  {
    key: "webAnalyticsVisits",
    label: "Web Analytics — Visits",
    type: "number",
    category: "audience",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "visits",
    description:
      "Total visit count (a single visitor may have multiple visits per day). Available on `web-analytics-stats` and `web-analytics-chart`.",
  },
  {
    key: "webAnalyticsSessionBounceRate",
    label: "Web Analytics — Session Bounce Rate",
    type: "percentage",
    category: "audience",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "webAnalyticsVisits" },
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "session_bounce_rate",
    description:
      "Percentage of single-page sessions (0-100). Renders as a gauge with green/yellow/red thresholds. Available on `web-analytics-stats` and `web-analytics-chart`.",
  },
  {
    key: "webAnalyticsAvgSessionLength",
    label: "Web Analytics — Avg Session Length",
    type: "number",
    category: "audience",
    dataType: "Duration",
    aggregationKind: "ratio", ratioComponents: { denominator: "webAnalyticsVisits" },
    compatibleWidgets: ["kpi"],
    apiField: "avg_session_length",
    description:
      "Average session length in seconds (decimal precision). Same underlying value as `avg_session_duration_sec` at higher precision. Available on `web-analytics-stats` and `web-analytics-chart`.",
  },
  {
    key: "webAnalyticsAvgSessionDurationSec",
    label: "Web Analytics — Avg Session Duration",
    type: "number",
    category: "audience",
    dataType: "Duration",
    aggregationKind: "ratio", ratioComponents: { denominator: "webAnalyticsVisits" },
    compatibleWidgets: ["kpi"],
    apiField: "avg_session_duration_sec",
    description:
      "Average session duration in whole seconds (integer). Same underlying value as `avg_session_length` at lower precision; widget config UI in Story 20.3 picks whichever the agency prefers. Available on `web-analytics-stats` and `web-analytics-chart`.",
  },

  // ==========================================================================
  // Story 35.8 — Long-tail catalogue closure: 4 batches × 26 metric defs
  // ==========================================================================
  //
  // Verified against [_bmad/bmm/data/integration-metrics/ahrefs.md] 2026-05-19
  // and the upstream Ahrefs v3 docs (Subscription Info, Rank Tracker
  // competitor-stats, Batch Analysis, Keywords Explorer Overview) on 2026-05-19.
  //
  // Batch 1 (+7): Subscription Info /limits-and-usage — NEW FREE endpoint,
  //   agency-facing API budget telemetry. Catalogue rows 219-225.
  // Batch 2 (+9): Rank Tracker /competitors-stats residual — existing FREE
  //   endpoint; default-select already widened in Story 20.2. Catalogue rows
  //   95, 96, 108, 110, 111, 114, 116, 117, 118.
  // Batch 3 (+7): Batch Analysis residual — existing paid endpoint; widens
  //   AHREFS_BATCH_ANALYSIS_DEFAULT_SELECT 33→39 (org_traffic already in
  //   baseline, so 6 net-new wire fields ship despite 7 catalogue rows).
  //   Catalogue rows 29, 37, 40,
  //   41, 48, 51, 52. `batchOrganicTraffic` (apiField `org_traffic`) shares
  //   the wire field with Story 20.1's `organicTraffic` — the metric-allowlist
  //   union-membership invariant does NOT require apiField uniqueness across
  //   metric defs (single field can surface in multiple MetricDefinitions
  //   when widget config wants a distinct preset semantic).
  // Batch 4 (+3): Keywords Explorer /overview long-tail — existing paid
  //   endpoint; widens KEYWORDS_EXPLORER_OVERVIEW_DEFAULT_SELECT 18→21.
  //   Catalogue rows 79, 93, 94.

  // --- Batch 1: Subscription Info /limits-and-usage (+7) -------------------
  {
    key: "subscriptionTier",
    label: "Subscription Tier",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "subscription",
    description:
      "Ahrefs subscription tier name (e.g. `Standard`, `Advanced`, `Enterprise`). FREE field from `/subscription-info/limits-and-usage`. Surfaces in the Story 20.4 API-budget gauge widget as a tier badge.",
  },
  {
    key: "subscriptionUsageResetDate",
    label: "Subscription Usage Reset Date",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "usage_reset_date",
    description:
      "ISO 8601 date when the workspace unit budget resets (monthly cadence per Ahrefs billing). Story 20.4 widget renders as a countdown KPI (e.g. `12 days to reset`).",
  },
  {
    key: "subscriptionUnitsLimitWorkspace",
    label: "Subscription Units Limit — Workspace",
    type: "number",
    category: "score",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "units_limit_workspace",
    description:
      "Total monthly API unit allowance across the workspace. Pairs with `units_usage_workspace` for the Story 20.4 workspace budget gauge.",
  },
  {
    key: "subscriptionUnitsUsageWorkspace",
    label: "Subscription Units Usage — Workspace",
    type: "number",
    category: "score",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "units_usage_workspace",
    description:
      "API units consumed across the workspace this billing cycle. Pairs with `units_limit_workspace` for the budget gauge.",
  },
  {
    key: "subscriptionUnitsLimitApiKey",
    label: "Subscription Units Limit — API Key",
    type: "number",
    category: "score",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "units_limit_api_key",
    description:
      "Per-API-key monthly unit allowance (may be lower than the workspace limit if the API key is provisioned with a sub-limit). Pairs with `units_usage_api_key`.",
  },
  {
    key: "subscriptionUnitsUsageApiKey",
    label: "Subscription Units Usage — API Key",
    type: "number",
    category: "score",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "units_usage_api_key",
    description:
      "API units consumed via the current API key this billing cycle. Pairs with `units_limit_api_key` for the per-key gauge.",
  },
  {
    key: "subscriptionApiKeyExpirationDate",
    label: "Subscription API Key Expiration Date",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "api_key_expiration_date",
    description:
      "ISO 8601 date when the API key expires. Story 20.4 widget renders as a countdown KPI; alerts when within 30 days of expiration.",
  },

  // --- Batch 2: Rank Tracker /competitors-stats residual (+9) --------------
  // Endpoint is FREE per Story 20.2 docs verification. Default-select already
  // contains all 24 documented fields per RANK_TRACKER_COMPETITORS_STATS_DEFAULT_SELECT.
  // Story 35.8 only adds the missing MetricDefinition rows for catalogue
  // closure (the wire fields are already plumbed through the existing
  // transformer per `transformers.ts:transformAhrefsRankTrackerCompetitorsStatsRow`).
  {
    key: "rankTrackerCompetitor",
    label: "Rank Tracker — Competitor",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "competitor",
    description:
      "Competitor domain name from the Rank Tracker project's competitor list. Surfaced via the Story 20.4 SoV detail widget (per-row dimension).",
  },
  {
    key: "rankTrackerAveragePosition",
    label: "Rank Tracker — Average Position",
    type: "number",
    category: "rankings",
    dataType: "Decimal",
    aggregationKind: "ratio", ratioComponents: { denominator: "targetPositionsCount" },
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "average_position",
    description:
      "Mean SERP position across the project's tracked keywords for this competitor. Float — partial-positions are valid (e.g. 4.3 = avg between #4 and #5).",
  },
  {
    key: "rankTrackerDiscussionsCount",
    label: "Rank Tracker — Discussions SERP-Feature Count",
    type: "number",
    category: "rankings",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "discussions_count",
    description:
      "Count of SERPs where Google rendered a `Discussions and forums` feature for the competitor. Surfaces in the Story 20.4 SoV detail widget.",
  },
  {
    key: "rankTrackerImagePackCount",
    label: "Rank Tracker — Image Pack SERP-Feature Count",
    type: "number",
    category: "rankings",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "image_pack_count",
    description:
      "Count of SERPs where Google rendered an `Image pack` (carousel of image results) for the competitor.",
  },
  {
    key: "rankTrackerKnowledgeCardCount",
    label: "Rank Tracker — Knowledge Card SERP-Feature Count",
    type: "number",
    category: "rankings",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "knowledge_card_count",
    description:
      "Count of SERPs where Google rendered a `Knowledge card` (factual single-answer block) referencing the competitor.",
  },
  {
    key: "rankTrackerThumbnailCount",
    label: "Rank Tracker — Thumbnail SERP-Feature Count",
    type: "number",
    category: "rankings",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "thumbnail_count",
    description:
      "Count of SERPs where the competitor's organic listing rendered with a thumbnail (video/recipe/product).",
  },
  {
    key: "rankTrackerVideoPreviewCount",
    label: "Rank Tracker — Video Preview SERP-Feature Count",
    type: "number",
    category: "rankings",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "video_preview_count",
    description:
      "Count of SERPs where Google rendered a video preview thumbnail for the competitor.",
  },
  {
    key: "rankTrackerVideosCount",
    label: "Rank Tracker — Videos SERP-Feature Count",
    type: "number",
    category: "rankings",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "videos_count",
    description:
      "Count of SERPs where Google rendered a `Videos` carousel referencing the competitor.",
  },
  {
    key: "rankTrackerXCount",
    label: "Rank Tracker — X (Twitter) SERP-Feature Count",
    type: "number",
    category: "rankings",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "x_count",
    description:
      "Count of SERPs where Google rendered an X (formerly Twitter) post carousel. Renamed from `twitter_count` mid-2024 per Ahrefs changelog.",
  },

  // --- Batch 3: Batch Analysis residual (+7) -------------------------------
  // Catalogue rows 29 (ahrefs_rank), 37 (ip), 40 (org_traffic / batch), 41
  // (org_cost / batch), 48 (org_traffic_top_by_country), 51 (paid_ads),
  // 52 (paid_cost / batch). Default-select widened from 33 → 39 unique
  // wire fields (org_traffic was already in the Story 20.1 baseline).
  // Note: `batchOrganicTraffic.apiField === "org_traffic"` overlaps with
  // Story 20.1's `organicTraffic` — the catalogue treats them as distinct
  // MetricDefinitions because the SAME wire field surfaces in 2 endpoints
  // (`/site-explorer/metrics` for `organicTraffic`; `/batch-analysis` for
  // `batchOrganicTraffic`). Widget config picks the right one via preset.
  {
    key: "batchAhrefsRank",
    label: "Batch — Ahrefs Rank",
    type: "number",
    category: "score",
    dataType: "Integer",
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi"],
    apiField: "ahrefs_rank",
    description:
      "Global Ahrefs Rank for the batch-analysis target (#1 = strongest backlink profile). Surfaces alongside `domainRating` in the multi-target dashboard preset.",
  },
  {
    key: "batchIpAddress",
    label: "Batch — IP Address",
    type: "number",
    category: "links",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "ip",
    description:
      "Primary IP address of the batch-analysis target. Useful for cross-target link-network investigation (multiple targets on the same IP = potential PBN).",
  },
  {
    key: "batchOrganicTraffic",
    label: "Batch — Organic Traffic",
    type: "number",
    category: "traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "org_traffic",
    description:
      "Estimated monthly organic traffic for the batch-analysis target. SAME wire field as `organicTraffic` on `/site-explorer/metrics`; distinct MetricDefinition for the batch-analysis preset surface.",
  },
  {
    key: "batchOrganicTrafficCost",
    label: "Batch — Organic Traffic Cost",
    type: "currency",
    category: "traffic",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "org_cost",
    description:
      "Equivalent paid-search cost (USD) of the target's organic traffic. Wire format is cents — converted to dollars by the transformer (`transformAhrefsCurrencyField`).",
  },
  {
    key: "batchOrganicTrafficTopByCountry",
    label: "Batch — Organic Traffic Top by Country",
    type: "number",
    category: "traffic",
    dataType: "Array",
    compatibleWidgets: ["data-table"],
    apiField: "org_traffic_top_by_country",
    description:
      "Opaque array of `{country, traffic}` objects representing the top-N countries by organic traffic. Surfaces as a data-table; per-element transformer deferred to Story 20.4 widget surface.",
  },
  {
    key: "batchPaidAdsCount",
    label: "Batch — Paid Ads Count",
    type: "number",
    category: "traffic",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "paid_ads",
    description:
      "Number of paid Google Ads currently associated with the batch-analysis target.",
  },
  {
    key: "batchPaidCost",
    label: "Batch — Paid Cost",
    type: "currency",
    category: "traffic",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "paid_cost",
    description:
      "Estimated monthly paid-search spend (USD) for the batch-analysis target. Wire format is cents — converted to dollars by the transformer.",
  },

  // --- Batch 4: Keywords Explorer /overview long-tail (+3) -----------------
  // Catalogue rows 79 (first_seen), 93 (volume_monthly), 94
  // (volume_monthly_history). Default-select widened from 18 → 21 fields.
  // The 3 paid fields add 10u each per keyword; the existing per-tenant
  // 5M/day budget gate covers them transparently. `volume_monthly_history`
  // ships as opaque Array<{date, value}>; the Story 20.3 line-chart widget
  // renders it via direct wire-key access (no per-element transformer).
  {
    key: "keywordsExplorerFirstSeen",
    label: "Keywords Explorer — First Seen",
    type: "number",
    category: "score",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "first_seen",
    description:
      "ISO 8601 date when Ahrefs first observed the keyword in its index. Useful for filtering newly-emerging keywords (e.g. seasonal queries, trending topics).",
  },
  {
    key: "keywordsExplorerVolumeMonthly",
    label: "Keywords Explorer — Volume Monthly",
    type: "number",
    category: "score",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "volume_monthly",
    description:
      "Latest-month search volume (scalar). Pairs with `volume_monthly_history` for the monthly-trend line-chart widget.",
  },
  {
    key: "keywordsExplorerVolumeMonthlyHistory",
    label: "Keywords Explorer — Volume Monthly History",
    type: "number",
    category: "score",
    dataType: "Array",
    compatibleWidgets: ["line-chart"],
    apiField: "volume_monthly_history",
    description:
      "Opaque array of `{date, value}` objects representing monthly search-volume history for the keyword. Story 20.3 line-chart widget renders via direct wire-key access.",
  },
];

// ============================================================================
// Dimension Definitions (AC #11 — 6 priority dimensions)
// ============================================================================

export const AHREFS_DIMENSIONS: DimensionDefinition[] = [
  {
    key: "target",
    label: "Target",
    type: "Content",
    exampleValues: ["example.com", "https://example.com/blog"],
  },
  {
    key: "country",
    label: "Country",
    type: "Geo",
    exampleValues: ["us", "gb", "ca", "au"],
  },
  {
    key: "mode",
    label: "Mode",
    type: "Tech",
    exampleValues: ["domain", "subdomain", "prefix", "exact_url"],
  },
  {
    key: "date",
    label: "Date",
    type: "Time",
    exampleValues: ["2026-05-01", "2026-05-02"],
  },
  {
    key: "language",
    label: "Language",
    type: "Demo",
    exampleValues: ["en", "es", "fr", "de"],
  },
  {
    key: "protocol",
    label: "Protocol",
    type: "Tech",
    exampleValues: ["http", "https"],
  },
  // ==========================================================================
  // Story 20.2 — +10 keyword-rank dimensions [AC #1, #16]
  // ==========================================================================
  {
    key: "keyword",
    label: "Keyword",
    type: "Search",
    exampleValues: ["seo tools", "best running shoes 2026", "claude code review"],
  },
  {
    key: "position",
    label: "SERP Position",
    type: "Search",
    exampleValues: ["1", "2-5", "6-10", "11-20"],
  },
  {
    key: "serp_features",
    label: "SERP Features",
    type: "Search",
    exampleValues: ["ai_overview", "featured_snippet", "knowledge_panel", "sitelinks"],
  },
  {
    key: "device",
    label: "Device",
    type: "Tech",
    exampleValues: ["desktop", "mobile"],
  },
  {
    key: "rank_tracker_project",
    label: "Rank Tracker Project",
    type: "Search",
    exampleValues: ["GreenLife Wellness", "TrendWear Fashion"],
  },
  {
    key: "rank_tracker_tag",
    label: "Rank Tracker Tag",
    type: "Classification",
    exampleValues: ["branded", "non-branded", "competitor"],
  },
  {
    key: "rank_tracker_location",
    label: "Rank Tracker Location",
    type: "Geo",
    exampleValues: ["United States", "United Kingdom", "California, US"],
  },
  {
    key: "volume_mode",
    label: "Volume Mode",
    type: "Tech",
    exampleValues: ["monthly", "average"],
  },
  {
    key: "intent_flag",
    label: "Search Intent Flag",
    type: "Classification",
    exampleValues: ["informational", "commercial", "transactional", "navigational", "branded", "local"],
  },
  {
    key: "best_position_kind",
    label: "Best Position Kind",
    type: "Search",
    exampleValues: ["organic", "ai_overview", "featured_snippet", "sitelink", "knowledge_card"],
  },
  // ==========================================================================
  // Story 35.8 — Backlinks dimensions flip gap → live (+4)
  // ==========================================================================
  // Per AC #10. Annotations point at the existing Story 20.1 helpers
  // (`/site-explorer/refdomains`, `/site-explorer/backlinks`,
  // `/site-explorer/anchors`). Per-row backlinks/anchors metric definitions
  // remain deferred to Story 20.4+ widget-config-driven helpers (200K+ row
  // paginated surfaces — same posture as Story 20.2d /page-explorer).
  {
    key: "refdomain",
    label: "Referring Domain",
    type: "Content",
    exampleValues: ["techcrunch.com", "forbes.com", "nytimes.com"],
  },
  {
    key: "url_from",
    label: "Source URL",
    type: "Content",
    exampleValues: [
      "https://techcrunch.com/2026/05/article-name",
      "https://forbes.com/sites/contributor/post-title",
    ],
  },
  {
    key: "url_to",
    label: "Target URL (link)",
    type: "Content",
    exampleValues: [
      "https://example.com/landing-page",
      "https://example.com/blog/post-title",
    ],
  },
  {
    key: "anchor",
    label: "Anchor Text",
    type: "Content",
    exampleValues: ["click here", "best SEO tools", "Ahrefs review 2026"],
  },
];

// ============================================================================
// Adapter implementation (AC #4, #5, #11)
// ============================================================================

/**
 * Per-endpoint estimated unit cost. Used for budget tracking when the response
 * headers don't carry `x-api-units-cost-total-actual` (Ahrefs occasionally
 * omits this on cache hits). Conservative defaults — actual costs come from
 * the response headers when present.
 */
const ENDPOINT_DEFAULT_COST: Record<string, number> = {
  "domain-rating": 50,
  "backlinks-stats": 50,
  "metrics": 50,
  "batch-analysis": 50,
  // Story 20.2a — autonomous sync default is 12 monthly rows × 4 metric fields
  // × 10 units = 480 units. We use 480 as the conservative default cost when
  // Ahrefs omits the `x-api-units-cost-total-actual` header (e.g. cache hits).
  "metrics-history": 480,
};

interface AhrefsAdapterEnv {
  readonly KV: KVStore;
}

/**
 * Type guard for the adapter's runtime env. Adapters keep their env shape
 * narrow per ADR-013; we expose just `KV` and read it through a guard.
 */
function getAdapterKv(ctx: AdapterContext): KVStore | null {
  const env = ctx.env as unknown as Partial<AhrefsAdapterEnv>;
  if (env && typeof env === "object" && env.KV) {
    return env.KV as KVStore;
  }
  return null;
}

// ============================================================================
// Story 20.3 — Fixture-mode helper (closes Epic 20 Ahrefs slice)
// ============================================================================

/**
 * Story 20.3 — Demo-mode fixture loader. Mirrors `loadRedditAdsFixture` at
 * `reddit-ads.ts:266-329` and `loadAmazonAdsFixture` at
 * `amazon-ads.ts:465-618` in structure.
 *
 * **Critical divergences from Reddit/Amazon (documented per Round 4):**
 *  1. **Single-target / single-clientSlug** — Ahrefs is a single-surface
 *     adapter (no `_adProduct`-style dispatch like Amazon's 4 surfaces, no
 *     `requestedLevel` filter). The entire fixture's `records[]` is returned
 *     post-date-range-filter.
 *  2. **Force-overwrite `dataFreshnessLagHours: 24`** — convention-stamping
 *     per Story 20.1 cache-TTL rationale (Ahrefs crawls at-most-daily).
 *  3. **Auth-mode-agnostic** — reads only `ctx.tenantId` + `ctx.clientId`,
 *     NOT `credentials.accessToken`. The future Ahrefs Connect OAuth (Story
 *     20.1a) will not require helper changes (Round 4 First Principles +
 *     AC #16 invariant).
 *
 * **Critical invariants (regression-tested in `ahrefs.test.ts`):**
 *  - Runs BEFORE `target` validation (`ahrefs.ts:2658`), `apiKey` validation
 *    (`:2666`), and unit-budget circuit-breaker (`:2678`). Caller MUST
 *    short-circuit at the very top of `fetchMetrics()` (Round 4 QA — fixture-
 *    mode-vs-production-mode separation).
 *  - **Cross-tenant leak protection** — resolves `clientSlug` via
 *    `getClientSlug(db, ctx.tenantId, ctx.clientId)` which enforces
 *    `(tenantId, clientId)` scoping at the SQL layer (Round 3 Red Team
 *    Attack #2). NEVER calls `getClientSlug(db, ctx.clientId)` without the
 *    tenantId scope.
 *  - **NEVER writes to the production cache prefix `ahrefs:*`** — read-only
 *    on `fixture:ahrefs:*` keys (Round 3 Red Team Attack #3).
 *  - **Structured-log telemetry per AC #21** — emits one of
 *    `ahrefs_demo_fixture_loaded` / `_missing` / `_binding_missing` per
 *    invocation so the support team can debug missing fixtures from
 *    Grafana without source-code access.
 */
async function loadAhrefsFixture(
  ctx: AdapterContext,
  dateRange: { start: Date; end: Date }
): Promise<AdapterResult<AdapterMetrics>> {
  const env = ctx.env as unknown as Record<string, unknown>;
  const kv = env.KV as unknown;
  const db = env.DB as unknown;

  // AC #21 — all 3 telemetry events use `console.log` per spec verbatim.
  // (Pass 1 review fix — was previously console.warn for miss + binding-
  // missing; the AC explicitly mandates console.log for all three.)
  if (!kv || typeof (kv as { get?: unknown }).get !== "function") {
    console.log(
      JSON.stringify({
        event: "ahrefs_demo_fixture_binding_missing",
        tenantId: ctx.tenantId,
        clientId: ctx.clientId,
        integrationId: ctx.integrationId,
        missingBinding: "kv",
        timestamp: new Date().toISOString(),
      })
    );
    return adapterErr({
      code: "permission_denied",
      message: "Missing KV binding — cannot load Ahrefs demo fixture",
      retryable: false,
    });
  }
  if (!db) {
    console.log(
      JSON.stringify({
        event: "ahrefs_demo_fixture_binding_missing",
        tenantId: ctx.tenantId,
        clientId: ctx.clientId,
        integrationId: ctx.integrationId,
        missingBinding: "db",
        timestamp: new Date().toISOString(),
      })
    );
    return adapterErr({
      code: "permission_denied",
      message: "Missing DB binding — cannot resolve Ahrefs demo client slug",
      retryable: false,
    });
  }

  // Validate date range per Pass 1 Edge Case Hunter finding — reversed
  // ranges silently return zero records and Invalid Date crashes via
  // toISOString. Return adapterErr for a typed AdapterResult instead.
  const startTime = dateRange.start?.getTime?.();
  const endTime = dateRange.end?.getTime?.();
  if (
    typeof startTime !== "number" ||
    !Number.isFinite(startTime) ||
    typeof endTime !== "number" ||
    !Number.isFinite(endTime)
  ) {
    return adapterErr({
      code: "invalid_request",
      message:
        "Ahrefs demo fixture: dateRange.start and dateRange.end must be finite Date instances",
      retryable: false,
    });
  }
  if (startTime > endTime) {
    return adapterErr({
      code: "invalid_request",
      message:
        "Ahrefs demo fixture: dateRange.start must be on or before dateRange.end",
      retryable: false,
    });
  }

  const { getFixtureFromKV, filterFixtureByDateRange } = await import(
    "../fixtures/kv-loader"
  );
  const { getClientSlug } = await import("../fixtures/demo-fetch");

  let clientSlug: string;
  try {
    // AC #15 / Round 3 Red Team Attack #2 — `getClientSlug` enforces the
    // `(tenantId, clientId)` scope at the SQL query layer. We MUST pass
    // both. Bypassing this resolver would silently leak fixture data
    // across tenants.
    clientSlug = await getClientSlug(
      db as Parameters<typeof getClientSlug>[0],
      ctx.tenantId,
      ctx.clientId
    );
  } catch (err) {
    return adapterErr({
      code: "account_not_found",
      message: `Ahrefs demo client not found: ${err instanceof Error ? err.message : String(err)}`,
      retryable: false,
    });
  }

  const startedAt = Date.now();
  const metrics = await getFixtureFromKV(
    kv as Parameters<typeof getFixtureFromKV>[0],
    "ahrefs",
    clientSlug
  );

  if (metrics === null) {
    console.log(
      JSON.stringify({
        event: "ahrefs_demo_fixture_missing",
        tenantId: ctx.tenantId,
        clientId: ctx.clientId,
        integrationId: ctx.integrationId,
        clientSlug,
        kvKey: `fixture:ahrefs:${clientSlug}`,
        timestamp: new Date().toISOString(),
      })
    );
    return adapterErr({
      code: "account_not_found",
      message: `Ahrefs demo fixture not yet uploaded for client ${clientSlug}; run pnpm fixtures:upload`,
      retryable: false,
    });
  }

  const filtered = filterFixtureByDateRange(
    metrics,
    dateRange.start,
    dateRange.end
  );

  // Force-overwrite `dataFreshnessLagHours` per Story 20.1 cache-TTL
  // convention (Ahrefs crawls at-most-daily). The rest of `metadata` is
  // preserved verbatim from the fixture.
  const result: AdapterMetrics = {
    ...filtered,
    metadata: {
      ...(filtered.metadata ?? {}),
      dataFreshnessLagHours: 24,
    },
  };

  console.log(
    JSON.stringify({
      event: "ahrefs_demo_fixture_loaded",
      tenantId: ctx.tenantId,
      clientId: ctx.clientId,
      integrationId: ctx.integrationId,
      clientSlug,
      recordCount: result.recordCount,
      durationMs: Date.now() - startedAt,
      timestamp: new Date().toISOString(),
    })
  );

  return adapterOk(result);
}

export const ahrefsAdapter: IntegrationAdapter = {
  platform: PLATFORM,
  authType: "api-key",
  rateLimits: {
    // 60/min is the documented Ahrefs steady-state RPS for Enterprise apps
    // per docs.ahrefs.com/api/docs/introduction. The unit-budget circuit-
    // breaker (AC #5) is a SEPARATE limit: a single batch-analysis call can
    // cost 180 units while a domain-rating call costs 50 — the queue throttle
    // counts requests, the circuit-breaker counts units.
    requestsPerMinute: 60,
    requestsPerDay: 100_000,
  },

  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    _dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    // Story 20.3 — fixture-mode short-circuit (AC #9). MUST run BEFORE any
    // production-path validation (target / apiKey / unit-budget) so a demo
    // workspace with no live Ahrefs Enterprise key still renders fixture
    // data without triggering token_invalid / account_not_found / quota
    // errors.
    const fixturesEnabled =
      (ctx as unknown as { fixtures?: boolean }).fixtures === true;
    if (fixturesEnabled) {
      return loadAhrefsFixture(ctx, _dateRange);
    }

    const target = ctx.accountId;
    if (!target) {
      return adapterErr({
        code: "account_not_found",
        message: "No Ahrefs target configured for this integration. Set the target via the integration settings.",
        retryable: false,
      });
    }
    const apiKey = credentials.accessToken;
    if (!apiKey || apiKey.length === 0) {
      return adapterErr({
        code: "token_invalid",
        message: "Ahrefs API key is missing or empty.",
        retryable: false,
      });
    }

    const kv = getAdapterKv(ctx);

    // Per-tenant unit-budget circuit-breaker (AC #5)
    if (kv) {
      const budget = await checkAhrefsUnitBudget(kv, ctx.tenantId);
      if (budget.exceeded) {
        // Story 20.1 AC #5: emit a structured-log event ONCE per breach so
        // the notification pipeline can pick it up. The marker key in
        // checkAhrefsUnitBudget guarantees firstBreach = true only on the
        // first call to cross the threshold today.
        if (budget.firstBreach) {
          console.log(
            JSON.stringify({
              event: "ahrefs_daily_unit_budget_exceeded",
              tenantId: ctx.tenantId,
              integrationId: ctx.integrationId,
              currentUnits: budget.current,
              limitUnits: budget.limit,
              timestamp: new Date().toISOString(),
            })
          );
        }
        return adapterErr({
          code: "rate_limited",
          message:
            `Daily Ahrefs unit budget exceeded (${budget.current}/${budget.limit}). ` +
            `Sync paused until UTC midnight to protect your monthly quota.`,
          retryable: false,
        });
      }
    }

    const mode: AhrefsMode = inferAhrefsMode(target);
    const callCtx = { integrationId: ctx.integrationId, tenantId: ctx.tenantId };

    // Parallel via Promise.all — each call wrapped in `.catch` so a synchronous
    // throw (e.g. an unexpected runtime error) can't fail the whole Promise.all.
    // Defensive carry-forward from PageSpeed pattern at pagespeed.ts:537-555.
    const safeDr = fetchAhrefsDomainRating(apiKey, target, mode, callCtx).catch(
      (err) =>
        adapterErr({
          code: "network_error" as const,
          message: sanitizeAhrefsErrorMessage(
            `domain-rating call threw: ${err instanceof Error ? err.message : String(err)}`,
            apiKey
          ),
          retryable: true,
        })
    );
    const safeBl = fetchAhrefsBacklinksStats(apiKey, target, mode, callCtx).catch(
      (err) =>
        adapterErr({
          code: "network_error" as const,
          message: sanitizeAhrefsErrorMessage(
            `backlinks-stats call threw: ${err instanceof Error ? err.message : String(err)}`,
            apiKey
          ),
          retryable: true,
        })
    );
    const safeMt = fetchAhrefsMetrics(apiKey, target, mode, callCtx).catch(
      (err) =>
        adapterErr({
          code: "network_error" as const,
          message: sanitizeAhrefsErrorMessage(
            `metrics call threw: ${err instanceof Error ? err.message : String(err)}`,
            apiKey
          ),
          retryable: true,
        })
    );
    const safeBa = fetchAhrefsBatchAnalysis(
      apiKey,
      [{ url: target, mode }],
      AHREFS_BATCH_ANALYSIS_DEFAULT_SELECT,
      callCtx
    ).catch(
      (err) =>
        adapterErr({
          code: "network_error" as const,
          message: sanitizeAhrefsErrorMessage(
            `batch-analysis call threw: ${err instanceof Error ? err.message : String(err)}`,
            apiKey
          ),
          retryable: true,
        })
    );

    // Story 20.2a — 5th endpoint: /metrics-history (12-month monthly default).
    // The autonomous-sync caller does not pass dateRange-specific bounds; we
    // anchor `date_from` 12 months back from today (UTC) so every customer
    // gets a baseline 12-month traffic-trend line on every sync without
    // widget config. Cost ≈ 480 units (12 monthly rows × 4 fields × 10 units).
    //
    // **mode is propagated from `inferAhrefsMode(target)` to keep this 5th
    // endpoint scoped consistently with the other 4 calls in this batch.**
    // Without explicit mode, the helper would default to "subdomains" (plural)
    // — the Ahrefs docs default — but that scope can include www./blog./shop./
    // etc., which would NOT match the (potentially "domain"-scoped) DR /
    // backlinks-stats / metrics / batch-analysis records returned by the same
    // sync. Cross-card scope mismatch is a visible UX bug.
    //
    // /metrics-by-country is NOT in autonomous sync per Story 20.2a AC #5 —
    // it requires a `date` snapshot picker that's widget-config-specific and
    // a single call can run ~6750 units which is too costly for a cron sync.
    const metricsHistoryDateFrom = computeAutonomousMetricsHistoryDateFrom();
    const safeMh = fetchAhrefsMetricsHistory(
      apiKey,
      {
        target,
        date_from: metricsHistoryDateFrom,
        select: METRICS_HISTORY_DEFAULT_SELECT,
        history_grouping: "monthly",
        mode,
        // protocol falls through to helper default ("both").
      },
      callCtx
    ).catch(
      (err) =>
        adapterErr({
          code: "network_error" as const,
          message: sanitizeAhrefsErrorMessage(
            `metrics-history call threw: ${err instanceof Error ? err.message : String(err)}`,
            apiKey
          ),
          retryable: true,
        })
    );

    const [drResult, blResult, mtResult, baResult, mhResult] = await Promise.all([
      safeDr,
      safeBl,
      safeMt,
      safeBa,
      safeMh,
    ]);

    const records: Array<Record<string, unknown>> = [];
    let totalUnits = 0;

    if (drResult.success) {
      records.push(transformAhrefsDomainRatingResponse(drResult.data.data, target, mode));
      totalUnits += extractActualCost(drResult.data, "domain-rating");
    }
    if (blResult.success) {
      records.push(transformAhrefsBacklinksStatsResponse(blResult.data.data, target, mode));
      totalUnits += extractActualCost(blResult.data, "backlinks-stats");
    }
    if (mtResult.success) {
      records.push(transformAhrefsMetricsResponse(mtResult.data.data, target, mode));
      totalUnits += extractActualCost(mtResult.data, "metrics");
    }
    if (baResult.success) {
      const rows = baResult.data.data.batch_analysis ?? [];
      records.push(...transformAhrefsBatchAnalysisResponse(rows, target, mode));
      totalUnits += extractActualCost(baResult.data, "batch-analysis");
    }
    if (mhResult.success) {
      const rows = mhResult.data.data.metrics ?? [];
      records.push(...transformAhrefsMetricsHistoryResponse(rows, target, mode));
      totalUnits += extractActualCost(mhResult.data, "metrics-history");
    }

    // If ALL five endpoints failed, surface the first error rather than
    // returning an empty record set. This preserves the "did the adapter run?"
    // signal for the cron health monitor.
    if (records.length === 0) {
      const firstErr =
        (!drResult.success && drResult.error) ||
        (!blResult.success && blResult.error) ||
        (!mtResult.success && mtResult.error) ||
        (!baResult.success && baResult.error) ||
        (!mhResult.success && mhResult.error);
      if (firstErr) {
        return adapterErr(firstErr);
      }
      return adapterErr({
        code: "invalid_response",
        message: "Ahrefs returned no data for any of the 5 endpoint groups.",
        retryable: false,
      });
    }

    // Roll up unit consumption to KV (AC #5)
    if (kv && totalUnits > 0) {
      try {
        await incrementAhrefsUnitsUsed(kv, ctx.tenantId, ctx.integrationId, totalUnits);
      } catch {
        // Telemetry must never block the data path.
      }
    }

    // Surface partial-failure warnings so the widget can render a banner
    // instead of "no data available."
    const warnings: Array<{ readonly reportType: string; readonly reason: string }> = [];
    if (!drResult.success) warnings.push({ reportType: "domain-rating", reason: drResult.error.message });
    if (!blResult.success) warnings.push({ reportType: "backlinks-stats", reason: blResult.error.message });
    if (!mtResult.success) warnings.push({ reportType: "metrics", reason: mtResult.error.message });
    if (!baResult.success) warnings.push({ reportType: "batch-analysis", reason: baResult.error.message });
    if (!mhResult.success) warnings.push({ reportType: "metrics-history", reason: mhResult.error.message });

    return adapterOk({
      platform: PLATFORM,
      accountId: target,
      dateRange: { start: new Date(), end: new Date() },
      records,
      fetchedAt: new Date(),
      recordCount: records.length,
      warnings: warnings.length > 0 ? warnings : undefined,
      metadata: {
        // Ahrefs lag is up to 24h for crawl-driven metrics; DR recalibrates ~weekly.
        dataFreshnessLagHours: 24,
        unitsConsumed: totalUnits,
        unitBudgetLimit: AHREFS_DAILY_TENANT_UNIT_BUDGET,
      },
    });
  },

  async validateConnection(
    _ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    const apiKey = credentials.accessToken;
    if (!apiKey || apiKey.length === 0) {
      return adapterErr({
        code: "token_invalid",
        message: "Ahrefs API key is missing or empty.",
        retryable: false,
      });
    }
    const result = await validateAhrefsConnection(apiKey);
    if (!result.success) {
      return adapterErr(result.error);
    }
    return adapterOk(true);
  },

  async discoverAccounts(
    ctx: AdapterContext,
    _credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    // Ahrefs has no concept of "ad accounts" or "properties" — the target IS
    // the account. We surface the configured target as a single AccountInfo;
    // when the customer has not yet picked a target (account_id == ""), we
    // return an empty list so the widget config flow prompts them.
    const accountId = ctx.accountId;
    if (!accountId) {
      return adapterOk([]);
    }
    return adapterOk([
      {
        id: accountId,
        name: accountId,
        platform: PLATFORM,
      },
    ]);
  },

  getAvailableMetrics(): MetricDefinition[] {
    return AHREFS_METRICS;
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return AHREFS_DIMENSIONS;
  },

  /**
   * Story 20.2 AC #19 — per-endpoint availability snapshot.
   *
   * Calls the FREE `/subscription-info/limits-and-usage` endpoint once,
   * parses the customer's `subscription` field, and maps each adapter
   * endpoint to one of `"available" | "subscription_required"`.
   *
   * Story 20.2 detects ONLY the Rank Tracker subscription gate.
   * Story 20.2d adds Site Audit endpoints (uniform Enterprise-tier gating).
   * Story 20.2e adds Brand Radar add-on detection via a single FREE probe to
   * `/sov-overview` with `prompts=custom` + `data_source=chatgpt` — the
   * cheapest possible BR call (FREE per docs because custom-prompts-only).
   * On 403 with body matching `BRAND_RADAR_SUBSCRIPTION_REGEX`, all 8 BR
   * endpoints flip to `subscription_required`.
   *
   * Story 20.2f adds Web Analytics overview surface — bundled with Ahrefs
   * Connect; no new add-on probe required. Both `web-analytics-stats` and
   * `web-analytics-chart` are marked `available` for any Ahrefs Connect
   * customer regardless of subscription tier (Lite, Standard, Advanced,
   * Enterprise). Per-project `web_analytics_data_key` presence is handled
   * at the widget-config layer (Story 20.3 will use
   * `filterAhrefsProjectsWithWebAnalytics` in the project picker).
   *
   * Story 35.8 adds Subscription Info `/limits-and-usage` — FREE endpoint,
   * available to any Ahrefs Connect customer; no add-on probe required.
   * Closes Epic 35 Ahrefs slice at 99.6% catalogue coverage. The endpoint
   * is marked `available` for any tier; the Story 20.4 widget UI surfaces
   * the agency-facing API-budget gauge.
   *
   * **Up to two upstream calls per invocation (both FREE):**
   *   1. `/subscription-info/limits-and-usage` (Story 20.1, FREE).
   *   2. `/brand-radar/sov-overview` BR probe (Story 20.2e, FREE via
   *      `prompts=custom`). Cached for 5 minutes (Code Review Pass 1 H4)
   *      keyed by `(tenantId, apiKeyDigest)` (Code Review Pass 2 M8) so
   *      multi-integration tenants on the same Ahrefs account share the
   *      cache slot. On cache HIT, only call #1 fires.
   */
  async discoverEndpoints(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<EndpointDiscoveryInfo>> {
    const apiKey = credentials.accessToken;
    if (!apiKey || apiKey.length === 0) {
      return adapterErr({
        code: "token_invalid",
        message: "Ahrefs API key is missing or empty.",
        retryable: false,
      });
    }
    // Pass logging context so the structured-log event from the underlying
    // free `/subscription-info/limits-and-usage` call carries the
    // tenantId + integrationId. Useful for correlating discovery probes.
    const result = await validateAhrefsConnection(apiKey, {
      tenantId: ctx.tenantId,
      integrationId: ctx.integrationId,
    });
    if (!result.success) {
      return adapterErr(result.error);
    }
    const subscription = (result.data.data.subscription ?? "").toLowerCase();
    const enterpriseTier =
      subscription.includes("enterprise") || subscription === "";
    // Heuristic: Rank Tracker is bundled with Enterprise per Ahrefs sales
    // collateral; non-Enterprise plans MAY have Rank Tracker as an add-on
    // but we can't introspect that without making a paid call. Conservative
    // default: assume non-Enterprise lacks Rank Tracker. The widget UI can
    // re-poll if the customer reports a gating mismatch.
    const rankTrackerStatus: "available" | "subscription_required" = enterpriseTier
      ? "available"
      : "subscription_required";

    // Story 20.2e — Brand Radar add-on probe. Single FREE call to
    // /sov-overview with `prompts=custom` (FREE per docs even on accounts with
    // zero custom prompts — returns empty `metrics: []`). On 403 with the BR
    // regex match, mark all 8 BR endpoints `subscription_required`. On any
    // other error, mark all 8 `available` (conservative — prefer false-available
    // to false-locked; the runtime safety net is `parseAhrefsApiError`).
    //
    // **Code Review Pass 3 (P3-L8)** — Brand Radar requires Standard+ tier
    // (per ahrefs.com/brand-radar verified 2026-05-05). Trial / Lite / Free
    // subscriptions cannot have BR. Skip the probe call entirely on those
    // tiers — saves 100-300ms per discovery flow on tenants where BR is
    // structurally impossible.
    const brStatus = subscription.includes("trial") || subscription.includes("lite")
      ? "subscription_required"
      : await probeBrandRadarAvailability(apiKey, ctx);

    const brSubscriptionMeta =
      brStatus === "subscription_required"
        ? {
            subscriptionRequired: "Brand Radar",
            setupHint:
              "Brand Radar requires a paid add-on starting at $199/month per AI platform — see ahrefs.com/brand-radar to enable.",
          }
        : {};

    return adapterOk({
      endpoints: [
        // Story 20.1 endpoints — always available on Enterprise (the only
        // tier we accept at connect time per `validateAhrefsKeyOrThrow`).
        { key: "domain-rating", status: "available" as const },
        { key: "backlinks-stats", status: "available" as const },
        { key: "metrics", status: "available" as const },
        { key: "batch-analysis", status: "available" as const },
        // Story 20.2 endpoints
        { key: "keywords-explorer-overview", status: "available" as const },
        { key: "organic-keywords", status: "available" as const },
        {
          key: "rank-tracker-competitors-stats",
          status: rankTrackerStatus,
          ...(rankTrackerStatus === "subscription_required"
            ? {
                subscriptionRequired: "Rank Tracker",
                setupHint:
                  "Rank Tracker requires an Ahrefs Enterprise plan or the Rank Tracker add-on.",
              }
            : {}),
        },
        { key: "keywords-history", status: "available" as const },
        // Story 20.2a endpoints
        { key: "metrics-history", status: "available" as const },
        { key: "metrics-by-country", status: "available" as const },
        // Story 20.2d Site Audit endpoints — uniform Enterprise-tier gating
        // (no per-add-on detection; public docs do not describe a separate
        // Site Audit add-on tier per 2026-05-05 verification).
        { key: "site-audit-projects", status: "available" as const },
        { key: "site-audit-issues", status: "available" as const },
        { key: "site-audit-page-explorer", status: "available" as const },
        { key: "site-audit-page-content", status: "available" as const },
        // Story 20.2e Brand Radar endpoints — add-on detection via the
        // /sov-overview FREE probe. 8 GET endpoints; 9 entries because
        // /sov-overview + /sov-history are 2 distinct surfaces.
        { key: "brand-radar-ai-responses", status: brStatus, ...brSubscriptionMeta },
        { key: "brand-radar-impressions-history", status: brStatus, ...brSubscriptionMeta },
        { key: "brand-radar-mentions-history", status: brStatus, ...brSubscriptionMeta },
        { key: "brand-radar-impressions-overview", status: brStatus, ...brSubscriptionMeta },
        { key: "brand-radar-mentions-overview", status: brStatus, ...brSubscriptionMeta },
        { key: "brand-radar-sov-overview", status: brStatus, ...brSubscriptionMeta },
        { key: "brand-radar-sov-history", status: brStatus, ...brSubscriptionMeta },
        { key: "brand-radar-cited-domains", status: brStatus, ...brSubscriptionMeta },
        { key: "brand-radar-cited-pages", status: brStatus, ...brSubscriptionMeta },
        // Story 20.2f — Web Analytics overview surface. Bundled with Ahrefs
        // Connect (every Ahrefs account, including the Free tier, gets WA up
        // to 1M events/month per site per
        // [help.ahrefs.com/en/articles/10247870-about-ahrefs-web-analytics]
        // verified 2026-05-06). No new add-on subscription probe needed —
        // status is `available` for any Ahrefs Connect customer regardless
        // of subscription tier (Lite, Standard, Advanced, Enterprise).
        // Per-project `web_analytics_data_key` presence is handled at the
        // widget-config layer (Story 20.3 will use
        // `filterAhrefsProjectsWithWebAnalytics` in the project picker).
        { key: "web-analytics-stats", status: "available" as const },
        { key: "web-analytics-chart", status: "available" as const },
        // Story 35.8 — Subscription Info `/limits-and-usage`. FREE endpoint;
        // available to any Ahrefs Connect customer regardless of subscription
        // tier (Lite, Standard, Advanced, Enterprise). No add-on probe needed
        // — the endpoint is guaranteed-available per docs verification
        // 2026-05-19. Agency-facing API budget telemetry surface for the
        // Story 20.4 widget UI.
        {
          key: "subscription-info-limits-and-usage",
          status: "available" as const,
        },
      ],
    });
  },
};

/**
 * Story 20.2e — single FREE probe to `/brand-radar/sov-overview` with
 * `prompts=custom` + `data_source=chatgpt`. Detects the Brand Radar add-on
 * gate without burning units (FREE per docs because custom-prompts-only).
 *
 * Mapping per AC #8:
 *   200 → all 8 BR endpoints `available`.
 *   403 + body matches `BRAND_RADAR_SUBSCRIPTION_REGEX` → all 8 `subscription_required`.
 *   Any other error (timeout, 5xx, generic plan-tier 403) → conservative
 *     `available` so the runtime safety net (`parseAhrefsApiError`) handles
 *     the 403 with the correct user-facing copy on the actual call.
 *
 * **5-minute KV cache (Code Review Pass 1, H4):** the probe result is
 * cached per `(tenantId, integrationId)` for 5 minutes (300s TTL). Connect
 * flows that poll discoverEndpoints multiple times in succession then re-use
 * the cached probe result, avoiding upstream call doubling. KV failures
 * (read or write) are intentionally swallowed — the data path must NOT
 * fail because of an observability/cache hiccup.
 *
 * Exported for tests; production consumers go through `discoverEndpoints`.
 */
const BRAND_RADAR_DISCOVERY_CACHE_TTL_SEC = 300;

/**
 * Stable short hash of the API key (Code Review Pass 2, M8). Brand Radar
 * subscription is account-scoped (the API key is the account credential),
 * so the discovery cache MUST key by API key — not integrationId. A tenant
 * with N integrations on the same Ahrefs account otherwise burns N redundant
 * probe calls per 5-min window for identical answers.
 *
 * 8-char hex digest of the SHA-256 hash — collision-resistant enough to
 * separate two accounts within the same tenant; short enough to keep KV
 * keys readable in logs. The key never appears in the cached value, only
 * the digest.
 *
 * **Code Review Pass 3 (P3-L3)** — defensive fallback for non-Workers
 * runtimes that may lack `crypto.subtle` (older test runners or
 * minimally-mocked global crypto). The fallback hash is non-cryptographic
 * but acceptable here because this is a CACHE KEY, not a security boundary
 * — false sharing across two API keys would only cross-pollinate cache
 * states for 5 minutes, no privilege escalation possible.
 */
async function digestAhrefsApiKeyShort(apiKey: string): Promise<string> {
  try {
    const bytes = new TextEncoder().encode(apiKey);
    const digest = await crypto.subtle.digest("SHA-256", bytes);
    const view = new Uint8Array(digest);
    let hex = "";
    for (let i = 0; i < 4; i++) {
      hex += view[i]!.toString(16).padStart(2, "0");
    }
    return hex;
  } catch {
    // Non-crypto fallback — DJB2-like 32-bit hash. Sufficient for cache-key
    // separation across distinct API keys; not a security boundary.
    let hash = 5381;
    for (let i = 0; i < apiKey.length; i++) {
      hash = ((hash << 5) + hash + apiKey.charCodeAt(i)) | 0;
    }
    return `fb${(hash >>> 0).toString(16).padStart(8, "0").slice(0, 6)}`;
  }
}

function buildBrandRadarDiscoveryCacheKey(
  tenantId: string,
  apiKeyDigest: string
): string {
  // Code Review Pass 2 (M8): keyed by `(tenantId, apiKeyDigest)` instead of
  // `(tenantId, integrationId)`. Same Ahrefs account ⇒ same digest ⇒ shared
  // cache slot, regardless of how many ProvenLeap integration rows point at it.
  return `discovery:ahrefs-br:${tenantId}:${apiKeyDigest}`;
}

async function probeBrandRadarAvailability(
  apiKey: string,
  ctx: AdapterContext
): Promise<"available" | "subscription_required"> {
  // Code Review Pass 2 (M8) — key by API-key digest, not integrationId, so
  // multiple integrations on the same Ahrefs account share the cache slot.
  const apiKeyDigest = await digestAhrefsApiKeyShort(apiKey);
  const cacheKey = buildBrandRadarDiscoveryCacheKey(ctx.tenantId, apiKeyDigest);
  const kv = (ctx.env as { KV?: unknown } | undefined)?.KV as
    | { get(k: string): Promise<string | null>; put(k: string, v: string, opts?: { expirationTtl?: number }): Promise<void> }
    | undefined;

  // Cache read — silently fall through on miss / KV error.
  if (kv) {
    try {
      const cached = await kv.get(cacheKey);
      if (cached === "available" || cached === "subscription_required") {
        return cached;
      }
    } catch {
      // KV read failure must NOT break discoverEndpoints.
    }
  }

  // The probe call is intentionally minimal: `data_source=chatgpt` (1
  // documented client; matches the cheapest billed-mode shape) and
  // `prompts=custom` (FREE — only customer-uploaded prompts, returns empty
  // `metrics: []` on accounts without any).
  const result = await fetchAhrefsBrandRadarSovOverview(
    apiKey,
    {
      data_source: ["chatgpt"],
      prompts: "custom",
    },
    {
      tenantId: ctx.tenantId,
      integrationId: ctx.integrationId,
    }
  );
  let status: "available" | "subscription_required";
  if (result.success) {
    status = "available";
  } else if (
    // Code Review Pass 3 (P3-L7) — re-detect using the SAME canonical regex
    // that errors.ts used to rewrite the message in the first place. The
    // rewritten message ("Your Ahrefs subscription doesn't include the Brand
    // Radar add-on…") satisfies the regex (`brand.?radar` + `add.?on`).
    // Using the canonical regex keeps probe and parseAhrefsApiError aligned —
    // a future regex change in one place automatically updates the other.
    result.error.code === "permission_denied" &&
    BRAND_RADAR_SUBSCRIPTION_REGEX.test(result.error.message)
  ) {
    status = "subscription_required";
  } else {
    // Conservative: prefer false-available on transient errors so the runtime
    // safety net handles the 403 with correct copy. Do NOT cache transient
    // errors — they should re-probe on the next discoverEndpoints call.
    return "available";
  }

  // Cache write — only when we got a definitive result. Swallow write errors.
  if (kv) {
    try {
      await kv.put(cacheKey, status, {
        expirationTtl: BRAND_RADAR_DISCOVERY_CACHE_TTL_SEC,
      });
    } catch {
      // Cache write failure must NOT break discoverEndpoints.
    }
  }

  return status;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Pick the actual unit cost from a successful call result. Falls back to
 * the per-endpoint default if Ahrefs omitted the header (cache hit, unknown
 * provider behavior, etc.).
 */
function extractActualCost<T>(
  result: AhrefsCallResult<T>,
  endpoint: keyof typeof ENDPOINT_DEFAULT_COST
): number {
  if (result.cost.cacheStatus === "hit" || result.cost.cacheStatus === "free") {
    // Cache-served responses are free per AC #5; documented-as-free
    // endpoints (Rank Tracker `/competitors-stats`, Subscription Info,
    // future Management endpoints) carry the synthetic `"free"` literal
    // per Story 20.2 AC #11 — both short-circuit to zero so the daily-
    // unit-budget rollup stays accurate.
    return 0;
  }
  if (result.cost.costTotalActual > 0) {
    return result.cost.costTotalActual;
  }
  if (result.cost.costTotalExpected > 0) {
    return result.cost.costTotalExpected;
  }
  return ENDPOINT_DEFAULT_COST[endpoint] ?? 50;
}

/**
 * Story 20.2a — compute the `date_from` value for the autonomous-sync
 * /metrics-history call. Anchors 12 months back from today (UTC) so every
 * customer gets a 12-month traffic-trend baseline on every sync without
 * needing widget config.
 *
 * UTC anchoring is intentional — Ahrefs serves data on UTC day boundaries
 * per docs.ahrefs.com, and using local time would produce off-by-one cache
 * keys for customers near midnight transitions. Exported for tests.
 */
export function computeAutonomousMetricsHistoryDateFrom(): string {
  const now = new Date();
  const date = new Date(
    Date.UTC(now.getUTCFullYear() - 1, now.getUTCMonth(), now.getUTCDate())
  );
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Re-export error mapping helpers so the connect route can use them.
export { ahrefsErrorTypeToAdapterCode, parseAhrefsApiError, sanitizeAhrefsErrorMessage };
